

import {
  OnInit,
  OnChanges,
  SimpleChanges,
  ChangeDetectorRef,
  OnDestroy,
  ElementRef} from '@angular/core';
import {
  Component,
  Input,
  CUSTOM_ELEMENTS_SCHEMA,
  Output,
  EventEmitter,
  Inject,
  HostListener,
  ViewChild
} from '@angular/core';
import { Router } from '@angular/router';
import { CommonModule } from '@angular/common';
import { RdsModule } from '@rabobank/rds-angular';
import { FormsModule } from '@angular/forms';
import { Batch, ColumnConfig } from '../../models/batch.interface';
import { ConnectorConfigService } from '../../services/connector-config.service';
import { BatchService } from '../../services/batch.service';
import { BatchStateService, BatchListState } from '../../services/batch-state.service';
import { MsalService } from '@azure/msal-angular';
import { ToastService, ToastType } from '../../services/toast.service';
import { NameFormatService } from '../../services/name-format.service';
import { forkJoin } from 'rxjs';
import { HttpEvent} from '@angular/common/http';
import { HttpEventType } from '@angular/common/http';
import { UploadBatchComponent } from '../upload-batch/upload-batch.component';

export interface BatchAction {
  action: 'verify' | 'reprocess' | 'delete';
  batch: Batch;
  batches?: Batch[]; // For bulk operations
}

@Component({
  selector: 'app-batch-table',
  standalone: true,
  imports: [CommonModule, RdsModule, FormsModule, UploadBatchComponent],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  template: `
    <div *ngIf="loading" class="loading-container">
      <p>Loading batches...</p>
    </div>

    <!-- Toasts are rendered globally in the app root via app-toast-outlet -->

    <!-- Page Header -->
    <div class="page-header" *ngIf="!loading">
      <h1 class="rds-heading-1">{{ getConnectorDisplayName() }} Batches</h1>
      <div class="header-actions">
        <div class="refresh-section">
          <app-upload-batch
            *ngIf="isVcfConnector || isPfeuConnector"
            [connector]="connector"
            (uploaded)="onUploadCompleted($event)"
            (uploadStatus)="onUploadStatusChange($event)"
            >Upload Batch</app-upload-batch
          >
      
          <rds-button-circular-link
            class="rds-display-inline-block rds-margin-inline-end-200"
            icon="rds-icon-arrow-circle-forward"
            size="xs"
            href="#"
            variant="primary-2"
            (click)="onRefresh($event)"
          >
            Refresh
          </rds-button-circular-link>
        </div>
        

        <!-- More Options - Only for GNM -->
        <div class="more-options-section" *ngIf="connector === 'GNM'">
          <div class="more-options-button" (click)="toggleMoreOptions()">
            <rds-icon name="rds-icon-three-dots-horizontal" size="xs" class="more-options-icon">
            </rds-icon>
            <span class="more-options-label">More options</span>
          </div>

          <!-- Dropdown Menu -->
          <div class="more-options-dropdown" *ngIf="showMoreOptions">
            <div class="dropdown-item" (click)="onAccountsClick()">
              <span>Accounts</span>
            </div>
            <div class="dropdown-item" (click)="onPasswordClick()">
              <span>Password</span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Search Filters -->
    <div class="search-filters" *ngIf="!loading">
      <div class="filter-row">
        <div class="filter-group">
          <rds-input-search
            id="batch-search"
            label="Search"
            minimum-match-characters-length="0"
            name="batch-search"
            placeholder="Search"
            search-icon-label="Search"
            [(ngModel)]="searchText"
            (ngModelChange)="onSearchChange($event)"
            (rdsKeyDown)="onSearchKeydown($event)"
            (rdsSearchButtonClick)="onRdsSearchButtonClick($event)"
          ></rds-input-search>
        </div>

        <div class="filter-group">
          <rds-select
            id="statusFilter"
            label="Status"
            name="statusFilter"
            placeholder="All statuses"
            [(ngModel)]="statusFilter"
            (ngModelChange)="onStatusChange($event)"
          >
            <rds-option *ngFor="let status of availableStatuses" [value]="status">{{
              status
            }}</rds-option>
          </rds-select>
        </div>

        <div class="filter-group" *ngIf="isPfeuConnector">
          <rds-select
            id="docTypeFilter"
            label="Document Type"
            name="docTypeFilter"
            placeholder="All types"
            [(ngModel)]="documentTypeFilter"
            (ngModelChange)="onDocumentTypeChange($event)"
          >
            <rds-option *ngFor="let dt of availableDocumentTypes" [value]="dt">{{ dt }}</rds-option>
          </rds-select>
        </div>

        <div class="filter-group" *ngIf="isVcfConnector">
          <rds-select
            id="uploadedByFilter"
            label="Uploaded by"
            name="uploadedByFilter"
            placeholder="All uploaders"
            [(ngModel)]="uploadedByFilter"
            (ngModelChange)="onUploadedByChange($event)"
          >
            <rds-option *ngFor="let uploader of availableUploadedBy" [value]="uploader">{{ uploader }}</rds-option>
          </rds-select>
        </div>

        <div class="filter-group">
          <rds-input-datepicker
            id="createdFrom"
            label="Upload start date"
            name="createdFrom"
            date-format="MM/DD/YYYY"
            placeholder="mm/dd/yyyy"
            locale="en-US"
            allow-weekend="true"
            date-disabled="false"
            blocked-days="[]"
            [(ngModel)]="createdFromDate"
            (ngModelChange)="onCreatedFromChange($event)"
          ></rds-input-datepicker>
        </div>

        <div class="filter-group">
          <rds-input-datepicker
            id="createdTo"
            label="Upload end date"
            name="createdTo"
            date-format="MM/DD/YYYY"
            placeholder="mm/dd/yyyy"
            locale="en-US"
            allow-weekend="true"
            date-disabled="false"
            blocked-days="[]"
            [(ngModel)]="createdToDate"
            (ngModelChange)="onCreatedToChange($event)"
          ></rds-input-datepicker>
        </div>

        <div class="filter-group clear-button-group">
          <rds-button
            variant="primary-default"
            size="medium"
            (click)="applyFilters()"
            (keydown.enter)="applyFilters()"
            (keydown.space)="applyFilters()"
          >
            Search
          </rds-button>
        </div>

        <div class="filter-group clear-button-group">
          <rds-label
            class="clear-filters-label"
            (click)="clearFilters()"
            role="button"
            tabindex="0"
            (keydown.enter)="clearFilters()"
            (keydown.space)="clearFilters()"
          >
            Clear filters
          </rds-label>
        </div>
      </div>
    </div>

    <!-- Results count shown when filters are applied -->
    <div class="results-header" *ngIf="isFilterApplied() && !loading">
      <div class="results-left">{{ total }} results</div>
    </div>

    <div
      class="vcf-upload-status"
      [class.completed]="vcfUploadState === 'completed'"
      *ngIf="!loading && showsUploadProgressBanner && (vcfUploadState === 'progress' || vcfUploadState === 'completed')"
    >
      <rds-icon
        [name]="
          vcfUploadState === 'progress'
            ? 'rds-icon-clipboard-checklist-pencil'
            : 'rds-icon-thumbs-up'
        "
        size="sm"
      ></rds-icon>
      <span class="vcf-upload-status-text">
        {{ vcfUploadState === 'progress' ? 'File upload is in progress...' : 'File upload completed.' }}
      </span>
    </div>

    <!-- Table and Pagination Container -->
    <div class="table-container" *ngIf="!loading">
      <rds-table horizontal-scroll>
        <table>
          <thead>
            <tr>
              <th class="checkbox-column">
                <rds-checkbox
                  [checked]="isAllSelected()"
                  (rdsChange)="masterToggle()"
                ></rds-checkbox>
              </th>
              <th
                *ngFor="let col of columns"
                [class.sort]="col.sortable !== false"
                [class.sort--asc]="isSorted(col, 'asc')"
                [class.sort--desc]="isSorted(col, 'desc')"
                (click)="onSort(col)"
              >
                <span class="sort-label">
                  {{ col.header }}
                  <rds-icon class="icon icon--asc" size="xs" name="rds-icon-caret-up"></rds-icon>
                  <rds-icon class="icon icon--desc" size="xs" name="rds-icon-caret-down"></rds-icon>
                </span>
              </th>
              <th class="actions-column"></th>
            </tr>
          </thead>
          <tbody>
            <tr
              *ngFor="let batch of displayedBatches; let i = index"
              [class.selected-row]="isSelected(batch)"
              [class.highlighted-batch]="batch.batchId === selectedBatchId"
            >
              <td class="checkbox-column">
                <rds-checkbox
                  [checked]="isSelected(batch)"
                  (rdsChange)="toggleSelection(batch)"
                ></rds-checkbox>
              </td>
              <td
                *ngFor="let col of columns"
                [attr.data-field]="col.field"
                [attr.title]="col.field === 'batchName' ? getCellValue(batch, col) : null"
              >
                <ng-container [ngSwitch]="col.type">
                  <span *ngSwitchCase="'date'" class="date-text">
                    {{ toDisplayDate(getCellValue(batch, col), col.field.toString(), col.format) | date: col.format || 'MM/dd/yyyy HH:mm' }}
                  </span>
                  <rds-tag
                    *ngSwitchCase="'status'"
                    [variant]="getStatusVariant(getCellValue(batch, col))"
                  >
                    <span class="status-text">{{ getCellValue(batch, col) }}</span>
                  </rds-tag>
                  <span *ngSwitchCase="'metadata'">
                    {{ getMetadataValue(batch, col.metadataField) }}
                  </span>
                  <span *ngSwitchDefault [class.batch-id-cell]="col.field === 'batchName'">
                    <ng-container *ngIf="col.field === 'batchName'; else normalCell">
                      <span
                        class="batch-link"
                        role="link"
                        tabindex="0"
                        (click)="onBatchNameClick(batch, $event)"
                        (keydown.enter)="onBatchNameClick(batch, $event)"
                      >
                        {{ getCellValue(batch, col) }}
                      </span>
                    </ng-container>
                    <ng-template #normalCell>{{ getCellValue(batch, col) }}</ng-template>
                  </span>
                </ng-container>
              </td>
              <td class="actions-column">
                <!-- Inline popover button (stays in DOM, moves with the row on scroll). Disabled when any checkbox selection exists. -->
                <div
                  class="action-button"
                  [class.disabled]="selection.length > 1"
                  (click)="onActionButtonClick(batch, $event)"
                  [attr.aria-disabled]="selection.length > 1"
                  aria-haspopup="true"
                  [attr.aria-expanded]="isPopoverOpen(batch)"
                >
                  <rds-icon name="rds-icon-three-dots-horizontal" size="xs"></rds-icon>

                  <!-- Inline popover content (positioned relative to the button to mirror rds-popover bottom-left) -->
                  <div
                    class="inline-popover"
                    [class.inline-popover--top]="shouldOpenPopoverAbove(batch, i)"
                    *ngIf="isPopoverOpen(batch)"
                    role="list"
                    (click)="$event.stopPropagation()"
                  >
                    <rds-item
                      *ngIf="canVerifyOnRow(batch)"
                      (click)="onAction('verify', batch)"
                      [attr.icon-start]="
                        getVerifyLabel(batch) === 'Verify'
                          ? 'rds-icon-check'
                          : 'rds-icon-info-circle'
                      "
                      role="listitem"
                    >
                      <span slot="label">{{ getVerifyLabel(batch) }}</span>
                    </rds-item>
                    <rds-item
                      *ngIf="canRowDownload(batch) && selection.length <= 1"
                      (click)="onDownloadSingleBatch(batch)"
                      icon-start="rds-icon-box-arrow-down"
                      role="listitem"
                    >
                      <span slot="label">Download</span>
                    </rds-item>
                    <rds-item
                      *ngIf="canReprocessOnRow(batch)"
                      (click)="onAction('reprocess', batch)"
                      icon-start="rds-icon-arrow-circle-forward"
                      role="listitem"
                    >
                      <span slot="label">Reprocess</span>
                    </rds-item>
                    <rds-item
                      (click)="onAction('delete', batch)"
                      icon-start="rds-icon-trash"
                      role="listitem"
                    >
                      <span slot="label">Delete</span>
                    </rds-item>
                    <rds-item
                      *ngIf="canClearOperatorOnRow(batch)"
                      (click)="onAction('clearOperator', batch)"
                      icon-start="rds-icon-pencil"
                      role="listitem"
                    >
                      <span slot="label">Claim Batch</span>
                    </rds-item>
                  </div>
                </div>
              </td>
            </tr>
            <tr *ngIf="displayedBatches.length === 0">
              <td [attr.colspan]="columns.length + 2" style="text-align: center; padding: 2rem;">
                {{ isFilterApplied() ? 'No records found' : 'No batches found' }}
              </td>
            </tr>
          </tbody>
        </table>
      </rds-table>

      <!-- Pagination & Page Size -->
      <div class="pagination-bar" *ngIf="total > 0" [class.with-footer]="showActionFooter">
        <div class="pagination-right">
          <div class="page-size">
            <rds-select [(ngModel)]="pageSize" (ngModelChange)="onPageSizeChange($event)">
              <rds-option *ngFor="let size of pageSizeOptions" [value]="size">
                {{ size }} Items per page
              </rds-option>
            </rds-select>
          </div>
          <div class="pagination-container">
            <rds-pagination
              [attr.total-count]="total"
              [attr.page]="pageIndex + 1"
              [attr.per-page]="pageSize"
              previous-label="Previous"
              next-label="Next"
              page-details-text="Page {a} of {b}"
              (rdsPageChange)="onPageChange($event)"
            ></rds-pagination>
          </div>
        </div>
      </div>
    </div>

    <!-- Selection Actions Footer -->
    <div class="selection-footer" *ngIf="showActionFooter && selection.length > 0">
      <div class="selection-cancel-row">
        <rds-icon
          name="rds-icon-times-circle-fill"
          size="xm"
          role="button"
          tabindex="0"
          (click)="clearSelections()"
          (keydown.enter)="clearSelections()"
          (keydown.space)="clearSelections()"
        ></rds-icon>
        <div class="selection-count-text">
          {{ selection.length }} {{ selection.length === 1 ? 'batch' : 'batches' }} selected
        </div>
      </div>
      <div class="selection-actions-row">
        <div class="selection-actions">
          <!-- Reordered action buttons: Verify, Claim Batch, Reprocess, Delete -->
          <rds-button
            *ngIf="canVerifyForSelection()"
            variant="primary-default"
            (click)="onBulkAction('verify')"
          >
            <rds-icon
              [name]="
                getVerifyLabelForSelection() === 'Verify'
                  ? 'rds-icon-check'
                  : 'rds-icon-info-circle'
              "
              size="xs"
            ></rds-icon>
            <span>{{ getVerifyLabelForSelection() }}</span>
          </rds-button>
          <rds-button
            *ngIf="canClearOperatorForSelection()"
            [variant]="
              canVerifyForSelection()
                ? pendingAction === 'clearOperator'
                  ? 'primary-default'
                  : 'secondary-default'
                : 'primary-default'
            "
            (click)="onBulkAction('clearOperator')"
          >
            <rds-icon name="rds-icon-pencil" size="xs"></rds-icon>
            <span>Claim Batch</span>
          </rds-button>
          <!-- Download Button: Only for VCF and status 'verify' -->
           <rds-button
             *ngIf="canDownloadForSelection()"
              [variant]="isDownloadPrimaryForSelection() ? 'primary-default' : 'secondary-default'"
              (click)="onBulkAction('download')"
            >
           <rds-icon name="rds-icon-box-arrow-down" size="xs"></rds-icon>
           <span>Download</span>
           </rds-button>

          <rds-button
            *ngIf="canReprocessForSelection()"
            [variant]="isReprocessPrimaryForSelection() ? 'primary-default' : 'secondary-default'"
            (click)="onBulkAction('reprocess')"
          >
            <rds-icon name="rds-icon-arrow-circle-forward" size="xs"></rds-icon>
            <span>Reprocess</span>
          </rds-button>

          <rds-button
            [variant]="pendingAction === 'delete' ? 'primary-default' : 'secondary-default'"
            (click)="onBulkAction('delete')"
          >
            <rds-icon name="rds-icon-trash" size="xs"></rds-icon>
            <span>Delete</span>
          </rds-button>
        </div>
      </div>
    </div>

    <!-- Delete Confirmation Dialog (RDS) -->
    <rds-dialog
      id="confirm"
      type="confirm"
      confirm-text="Yes, delete"
      cancel-text="Cancel"
      [presented]="showDeleteDialog"
      (rdsDialogDidDismiss)="onConfirmDialogDismiss($event)"
    >
      <span slot="header">
        <span slot="header">Delete batch</span>
      </span>
      <span slot="body" class="rds-paragraph">
        Are you sure you want to delete the selected batches?
      </span>
    </rds-dialog>

    <rds-dialog
      id="confirm-claim"
      type="confirm"
      confirm-text="Yes Claim"
      cancel-text="Go back"
      [presented]="showClaimDialog"
      (rdsDialogDidDismiss)="onClaimDialogDismiss($event)"
    >
      <span slot="header">Are you sure you want to claim batch?</span>
    </rds-dialog>

    <!-- Download confirmation when metadata incomplete -->
    <rds-dialog
      id="download-incomplete-confirm"
      type="confirm"
      confirm-text="Yes, download"
      cancel-text="Cancel"
      [presented]="showDownloadConfirmDialog"
      (rdsDialogDidDismiss)="onDownloadDialogDismiss($event)"
    >
      <span slot="header">Proceed with Download ?</span>
      <span slot="body" class="rds-paragraph">
        {{ downloadConfirmMessage }}
        <rds-message
          *ngIf="isVcfConnector"
          class="download-ai-message"
          variant="info"
          dismissible="false"
        >
        Data is captured using AI technology and may contain errors. Please review the files and make changes within the batch if necessary.
        </rds-message>
      </span>
    </rds-dialog>

    <!-- Hold-download informational alert dialog: shown when a Hold batch is owned by another user -->
    <rds-dialog
      id="hold-download"
      type="alert"
      confirm-text="OK"
      [presented]="showHoldDownloadDialog"
      (rdsDialogDidDismiss)="onHoldDialogDismiss($event)"
    >
      <span slot="header">Download blocked</span>
      <span slot="body" class="rds-paragraph">
        {{ holdMessagePrefix }} and assigned to {{ holdOwnerMessage }}. Kindly claim the batch first to enable downloading.
      </span>
    </rds-dialog>
  `,
  styles: [
    `
      :host {
        display: block;
        width: 100%;
        background-color: #f5f5f5;
        padding: 1.5rem 1.5rem; /* add left/right margins so content isn't flush to edge */
        box-sizing: border-box;
        overflow: visible;
      }
      .download-ai-message {
        display: block;
        margin-top: 1rem;
      }
      @media (max-width: 768px) {
        :host {
          padding: 0.75rem 1rem; /* smaller horizontal padding on small screens */
        }
      }
      .toast-container {
        position: fixed;
        left: 50%;
        transform: translate(-50%, -20%);
        z-index: 1100;
        display: flex;
        flex-direction: column;
        gap: 0.5rem;
        align-items: center;
        pointer-events: none; /* allow clicks to pass through except on toast items */
        max-width: calc(100% - 2rem);
        width: auto;
      }

      .toast-item {
        pointer-events: auto;
        max-width: 720px;
        width: 100%;
      }

      .results-header {
        display: flex;
        align-items: center;
        justify-content: flex-start;
        padding: 0.8rem;
        color: #374151;
        font-weight: 600;
        background-color: white;
        border-radius: 0.375rem 0.375rem 0 0;
      }
      .results-left {
        font-size: 0.95rem;
      }

      .vcf-upload-status {
        display: flex;
        align-items: center;
        gap: 0.5rem;
        padding: 0.625rem 0.75rem;
        margin-bottom: 0.75rem;
        border-radius: 0.375rem;
        border: 1px solid #dbeafe;
        background: #eff6ff;
        color: #1e3a8a;
      }

      .vcf-upload-status.completed {
        border-color: #bbf7d0;
        background: #f0fdf4;
        color: #166534;
      }

      .vcf-upload-status-text {
        font-size: 0.9rem;
        font-weight: 500;
      }

      /* Clear filters label styled as underlined link */
      .clear-filters-label {
        color: #000ea8; /* RDS primary / link blue */
        text-decoration: underline;
        cursor: pointer;
        font-weight: 500;
        padding: 0; /* remove button-like padding */
        margin-left: 0.75rem;
        display: inline-block;
        background: transparent;
        margin-bottom: 1rem;
      }

      .refresh-section {
        display: flex;
        align-items: center;
        gap: 0.5rem;
        margin-bottom: 0.5rem;
      }

      .more-options-section {
        position: relative;
        display: flex;
        align-items: center;
      }

      .more-options-button {
        display: flex;
        align-items: center;
        gap: 0.5rem;
        cursor: pointer;
      }

      .refresh-label {
        font-size: 0.875rem;
        color: #374151;
        cursor: pointer;
      }

      .more-options-icon {
        cursor: pointer;
        color: #6b7280;
        background-color: #ffffff;
        border-radius: 50%;
        padding: 0.25rem;
        transition: background-color 0.2s ease;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        border: 1px solid #d1d5db;
      }

      .more-options-icon:hover {
        background-color: #f9fafb;
      }

      .more-options-label {
        font-size: 0.875rem;
        color: #374151;
        cursor: pointer;
      }

      .more-options-dropdown {
        position: absolute;
        top: 100%;
        right: 0;
        margin-top: 0.5rem;
        background: white;
        border: 1px solid #d1d5db;
        border-radius: 0.375rem;
        box-shadow:
          0 10px 15px -3px rgba(0, 0, 0, 0.1),
          0 4px 6px -2px rgba(0, 0, 0, 0.05);
        min-width: 120px;
        z-index: 1000;
      }

      .dropdown-item {
        padding: 0.75rem 1rem;
        cursor: pointer;
        transition: background-color 0.2s ease;
        border-bottom: 1px solid #f3f4f6;
      }

      .dropdown-item:last-child {
        border-bottom: none;
      }

      .dropdown-item:hover {
        background-color: #f9fafb;
      }

      .dropdown-item span {
        font-size: 0.875rem;
        color: #374151;
      }
      rds-table {
        display: block;
        width: 100%;
      }
      table {
        width: 100%;
        border-collapse: collapse;
      }
      th {
        text-align: left;
        font-weight: 600;
        padding: 0.5rem 0.75rem; /* Matched padding with td for consistency */
        background-color: #ffffff;
        border-bottom: 2px solid #ddd;
        vertical-align: middle;
        overflow: hidden;
        white-space: nowrap;
        text-overflow: ellipsis;
      }
      /* Checkbox header cell: use flex to vertically center checkbox and label alignment */
      th.checkbox-column {
        width: 100px;
        text-align: center;
        vertical-align: middle;
        box-sizing: border-box;
      }

      th.actions-column {
        width: 80px;
        text-align: center;
      }
      td {
        padding: 0.5rem 0.75rem; /* Further reduced padding for optimal 10-row fit */
        // border-bottom: 1px solid #eee;
        vertical-align: middle;
      }
      /* Checkbox data cell: match header padding and center */
      td.checkbox-column {
        text-align: center;
        vertical-align: middle;
        box-sizing: border-box;
      }

      /* Ensure the RDS checkbox element is visually centered and slightly nudged if needed */
      th.checkbox-column rds-checkbox,
      td.checkbox-column rds-checkbox {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        height: auto;
        margin: 0;
        transform: translateY(12px); /* increased vertical nudge to align with text baseline */
      }

      /* Fallback for native checkbox inputs inside those cells */
      th.checkbox-column input[type='checkbox'],
      td.checkbox-column input[type='checkbox'] {
        transform: translateY(12px);
        margin: 0;
      }
      td.actions-column {
        text-align: center;
        position: relative;
      }
      .action-button {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        cursor: pointer;
        padding: 0.25rem;
        border-radius: 4px;
        position: relative; /* make popover position relative to this button */
      }
      .action-button:focus {
        outline: 2px solid rgba(37, 99, 235, 0.2);
      }
      .action-button.disabled {
        opacity: 0.55;
        cursor: pointer;
        background-color: transparent;
        pointer-events: auto; /* keep pointer events so aria-disabled works, but visually disabled */
      }
      .action-button.disabled rds-icon {
        color: #9ca3af; /* muted icon color */
      }
      .action-button.disabled:hover {
        background-color: transparent; /* avoid hover highlight */
      }
      .inline-popover {
        position: absolute;
        top: calc(100% + 0.25rem);
        right: 0; /* default: position below trigger (fallback) */
        left: auto;
        max-width: 320px; /* prevent extreme expansion */
        width: max-content;
        background: #fff;
        border: 1px solid #e5e7eb;
        border-radius: 6px;
        box-shadow:
          0 10px 15px -3px rgba(0, 0, 0, 0.1),
          0 4px 6px -2px rgba(0, 0, 0, 0.05);
        z-index: 1100;
        padding: 0.25rem 0;
        box-sizing: border-box;
        white-space: normal;
        overflow: hidden;
      }

      /* When menu should open above the trigger (works for last-row case) */
      .inline-popover--top {
        top: auto;
        bottom: calc(100% + 0.25rem);
      }
      .inline-popover rds-item {
        // display: flex;
        align-items: center;
        gap: 0.5rem;
        padding: 0.5rem 0.75rem;
        cursor: pointer;
        white-space: nowrap; /* keep labels on single line */
      }
      tbody tr {
        height: 3.2rem; /* Optimized row height for 10-row display */
      }

      thead tr {
        height: 3rem; /* Fixed header height */
      }
      /* Ensure batch table headers use semibold weight */
      .table-container table thead th {
        font-weight: 600 !important;
      }
      tbody tr:hover {
        background-color: #f9f9f9;
      }
      tbody tr.selected-row {
        background-color: #e3f2fd;
      }
      tbody tr.selected-row {
        background-color: transparent; /* remove blue highlight — keep checkbox visual only */
      }
      input[type='checkbox'] {
        cursor: pointer;
        width: 18px;
        height: 18px;
      }
      .status-completed,
      .status-paid,
      .status-success {
        color: #22c55e;
        font-weight: 500;
      }
      .status-pending,
      .status-processing {
        color: #f59e0b;
        font-weight: 500;
      }
      .status-failed,
      .status-rejected,
      .status-error {
        color: #ef4444;
        font-weight: 500;
        color: #666;
      }
      rds-table {
        display: block;
        width: 100%;
      }

      /* Enable native horizontal scrolling when the horizontal-scroll attribute is used */
      rds-table[horizontal-scroll] {
        overflow: auto;
        -webkit-overflow-scrolling: touch;
      }

      /* Prevent table cells from wrapping when horizontal scrolling is enabled */
      rds-table[horizontal-scroll] table {
        white-space: nowrap;
      }
      table {
        width: 100%;
        border-collapse: collapse;
      }
      th {
        text-align: left;
        font-weight: 600;
        padding: 1rem;
        background-color: #ffffff;
        border-bottom: 2px solid #ddd;
        overflow: hidden;
        white-space: nowrap;
        text-overflow: ellipsis;
      }
      td {
        padding: 0.875rem 1rem;
        border-bottom: 1px solid #eee;
      }

      /* Fixed width for batch name column with ellipsis */
      td[data-field='batchName'] {
        max-width: 200px;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        position: relative;
      }

      /* Tooltip for batch name on hover */
      td[data-field='batchName']:hover::after {
        content: attr(title);
        position: absolute;
        top: -2rem;
        left: 0;
        color: white;
        padding: 0.5rem;
        border-radius: 4px;
        font-size: 0.875rem;
        white-space: nowrap;
        z-index: 1000;
      }
      tbody tr.selected-row {
        background-color: transparent; /* remove blue highlight — keep checkbox visual only */
      }
      .status-completed,
      .status-paid,
      .status-success {
        color: #22c55e;
        font-weight: 500;
      }
      .status-pending,
      .status-processing {
        color: #f59e0b;
        font-weight: 500;
      }
      .status-failed,
      .status-rejected,
      .status-error {
        color: #ef4444;
        font-weight: 500;
      }

      .selection-footer {
        position: fixed;
        bottom: 0;
        left: 0;
        right: 0;
        background: white;
        border-top: 1px solid #f1f2f4ff;
        box-shadow: 0 -4px 12px rgba(0, 0, 0, 0.15);
        padding: 1rem 2rem;
        display: flex;
        flex-direction: column;
        justify-content: flex-start;
        align-items: flex-start;
        gap: 0.5rem;
        z-index: 1000;
        animation: slideUp 0.3s ease-out;
      }

      @keyframes slideUp {
        from {
          transform: translateY(100%);
          opacity: 0;
        }
        to {
          transform: translateY(0);
          opacity: 1;
        }
      }

      .selection-info {
        display: flex;
        align-items: center;
        gap: 1rem;
      }

      .selection-count {
        font-size: 1rem;
        font-weight: 600;
        color: #1e293b;
      }

      .selection-count-text {
        margin-left: 0.75rem;
        display: inline-flex;
        align-items: center;
        gap: 0.15rem;
      }

      .selection-cancel-row {
        display: flex;
        align-items: center; /* vertically center icon and text */
      }

      .selection-cancel-row rds-icon {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        vertical-align: middle;
        color: #000ea8; /* brand blue for cancel icon */
        cursor: pointer;
      }

      .action-description {
        font-size: 0.9rem;
        color: #64748b;
        font-style: italic;
      }

      .selection-actions-row {
        width: 100%;
        display: flex;
        justify-content: flex-start;
      }

      .selection-actions {
        display: flex;
        gap: 0.75rem;
        align-items: center;
      }

      .selection-actions rds-button {
        min-width: 120px;
        height: 40px;
        display: flex;
        align-items: center;
        gap: 0.75rem;
      }

      .selection-actions rds-button rds-icon {
        flex-shrink: 0;
        margin-right: 0.35rem;
      }

      .selection-actions rds-button span {
        margin-left: 0.25rem;
      }

      .pagination-container {
        display: flex;
        justify-content: flex-end;
        padding: 0.5rem 1rem;
      }

      .pagination-container.with-footer {
        margin-bottom: 72px; /* keep above fixed footer */
      }

      .pagination-bar {
        display: flex;
        align-items: center;
        justify-content: flex-end;
        padding: 0.9rem;
        background-color: white;
        border-top: 1px solid #e5e7eb;
        flex-shrink: 0; /* Prevent pagination from shrinking */
        max-height: 40px;
      }

      .pagination-bar.with-footer {
        margin-bottom: 72px;
      }

      .page-size label {
        margin-right: 0.5rem;
        font-size: 0.85rem;
        color: #475569;
      }

      .pagination-right {
        display: flex;
        align-items: center;
        gap: 1rem;
      }

      .page-size {
        display: flex;
        align-items: center;
        margin-top: 1.4rem;
      }

      .page-size rds-select {
        min-width: 180px;
      }

      .pagination-container {
        display: flex;
        align-items: center;
      }

      /* Base styling for status pills */
      .status-pill {
        display: inline-block;
        padding: 0.125rem 0.5rem;
        border-radius: 4px;
        font-weight: 600;
        font-size: 0.85rem;
        line-height: 1.4;
        white-space: nowrap;
      }

      /* When using RDS background utility classes */
      .status-pill.status-pill-bg {
        color: #374151;
        font-weight: 600;
      }

      /* Specific background colors for different status types */
      .status-pill.rds-color-background-info {
        background-color: #c2ecff !important; /* Verify status background */
      }

      .status-pill.rds-color-background-success {
        background-color: #e0e0e1 !important; /* Completed status background - same as new */
      }

      .status-pill.rds-color-background-warning {
        background-color: #ffe6b8 !important; /* Hold status background */
      }

      .status-pill.rds-menu-color-background-subtle {
        background-color: #e0e0e1 !important; /* New/default status background */
      }

      .status-pill.rds-color-background-danger {
        background-color: #fecfc3 !important; /* Error_extraction status background */
      }

      /* Prevent status values from wrapping into multiple lines */
      td[data-field='status'] rds-tag,
      td[data-field='status'] .status-text {
        white-space: nowrap;
        display: inline-block;
        overflow: hidden;
        text-overflow: ellipsis;
        max-width: 100%;
        vertical-align: middle;
      }

      /* Prevent date columns from wrapping (Captured on, Last updated) */
      td[data-field='datetime'] .date-text,
      td[data-field='lastModifiedDate'] .date-text {
        white-space: nowrap;
        display: inline-block;
        overflow: hidden;
        text-overflow: ellipsis;
        max-width: 100%;
        vertical-align: middle;
      }

      /* When using RDS border color utility classes */
      .status-pill.status-pill-border {
        background: #ffffff;
        border-width: 1px;
        border-style: solid;
        /* border color is provided by the attached RDS token class */
      }

      /* Sorting header styles (mirroring RDS example) */
      th.sort {
        cursor: pointer;
        user-select: none;
      }
      th.sort .sort-label {
        display: inline-flex;
        align-items: center;
        gap: 0.25rem;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        max-width: 100%;
      }
      th.sort .icon {
        pointer-events: none;
        display: inline-block;
        vertical-align: middle;
        color: #000faa;
      }
      th.sort.sort--asc .icon--asc {
        display: inline-block;
      }
      th.sort.sort--asc .icon--desc {
        display: none;
      }
      th.sort.sort--desc .icon--desc {
        display: inline-block;
      }
      th.sort.sort--desc .icon--asc {
        display: none;
      }
      th.sort:not(.sort--asc, .sort--desc) .icon {
        color: #000faa;
      }
      th.sort:not(.sort--asc, .sort--desc) .icon--asc {
        display: none;
      }

      /* Table Container - unified table and pagination */
      .table-container {
        background-color: white;
        overflow: hidden;
        flex: 1;
        display: flex;
        flex-direction: column;
        min-height: 0; /* Allow flex children to shrink */
      }

      /* Search Filters Styling */
      .search-filters {
        background-color: white;
        padding: 1.5rem; /* Increased padding for better spacing */
        border-radius: 6px;
        margin-bottom: 0.75rem; /* Reduced margin */
        box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
      }

      .filter-row {
        display: flex;
        flex-wrap: wrap;
        gap: 0.5rem; /* Reduced gap for more compact layout */
        align-items: flex-end;
      }

      .filter-group {
        flex: 1;
        min-width: 184px;
        max-width: 250px;
        display: flex;
        flex-direction: column;
        max-height: 69px;
      }

      .filter-group rds-input-search,
      .filter-group rds-select,
      .filter-group rds-input,
      .filter-group rds-input-datepicker {
        width: 100%;
        align-self: flex-end;
      }

      /* Custom margin for Status select only */
      .filter-group #statusFilter {
        margin: var(--rds-input-margin, var(--rds-form-elements-margin, 0 0 -4px));
      }

      .clear-button-group {
        display: flex;
        align-items: flex-end;
        justify-content: flex-start;
        flex: 0 0 auto;
        min-width: auto;
        padding-top: 0.25rem;
      }

      .clear-button-group rds-button {
        white-space: nowrap;
      }

      .filter-label {
        font-size: 0.875rem;
        font-weight: 600;
        color: #334155;
      }

      .filter-input,
      .filter-input-date,
      .filter-select {
        padding: 0.5rem 0.75rem;
        border: 1px solid #cbd5e1;
        border-radius: 4px;
        font-size: 0.875rem;
        background-color: #fff;
        transition: border-color 0.2s;
      }

      .filter-input:focus,
      .filter-input-date:focus,
      .filter-select:focus {
        outline: none;
        border-color: #2563eb;
        box-shadow: 0 0 0 2px rgba(37, 99, 235, 0.1);
      }

      .filter-input {
        min-width: 250px;
      }

      .filter-input-date {
        min-width: 150px;
      }

      .filter-select {
        min-width: 180px;
        cursor: pointer;
      }

      .clear-filters-btn {
        display: inline-flex;
        align-items: center;
        gap: 0.5rem;
        padding: 0.5rem 1rem;
        background-color: #fff;
        border: 1px solid #cbd5e1;
        border-radius: 4px;
        font-size: 0.875rem;
        font-weight: 500;
        color: #475569;
        cursor: pointer;
        transition: all 0.2s;
        height: 38px;
      }

      .clear-filters-btn:hover {
        background-color: #f1f5f9;
        border-color: #94a3b8;
      }

      .clear-filters-btn:active {
        transform: scale(0.98);
      }

      /* Batch Name column - wider for longer names */
      th:has(.batch-id-cell),
      td:has(.batch-id-cell) {
        min-width: 250px;
        max-width: 350px;
      }

      /* Batch ID styling */
      .batch-id-cell {
        color: var(--rds-link-color-text);
        font-weight: 500;
        word-break: break-word;
      }

      .batch-link {
        color: var(--rds-link-color-text);
        cursor: pointer;
        text-decoration: none; /* no underline */
        outline: none;
      }
      .batch-link:hover,
      .batch-link:focus {
        text-decoration: none;
        border-radius: 4px;
      }

      /* Highlighted batch styling */
      .highlighted-batch {
        background-color: #eff6ff !important;
        border-left: 3px solid #2563eb;
      }
      .highlighted-batch:hover {
        background-color: #dbeafe !important;
      }

      /* Enhanced styling when batch is both selected and highlighted */
      .highlighted-batch.selected-row {
        background-color: #e0f2fe !important;
        border-left: 4px solid #0369a1;
        box-shadow: 0 1px 3px rgba(3, 105, 161, 0.1);
      }

      /* Force-disable visual row highlight when rows are selected via checkbox */
      table tbody tr.selected-row,
      table tbody tr.selected-row:hover,
      table tbody tr.highlighted-batch.selected-row {
        background-color: transparent !important;
        border-left: none !important;
        box-shadow: none !important;
      }

      /* Disable row hover background and color change */
      table tbody tr:hover,
      table tbody tr:hover td,
      table tbody tr:hover th {
        background-color: transparent !important;
        color: inherit !important;
        box-shadow: none !important;
      }
      .highlighted-batch.selected-row:hover {
        background-color: #cffafe !important;
      }

      /* Remove highlighted-batch visuals (left border and light-blue background) entirely */
      .highlighted-batch,
      .highlighted-batch:hover,
      tr.highlighted-batch,
      tr.highlighted-batch:hover,
      .highlighted-batch.selected-row,
      .highlighted-batch.selected-row:hover {
        background-color: transparent !important;
        border-left: none !important;
        box-shadow: none !important;
      }

      /* Dialog Overlay Styles */
      .dialog-overlay {
        position: fixed;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background: rgba(0, 0, 0, 0.5);
        display: flex;
        justify-content: center;
        align-items: center;
        z-index: 1000;
      }

      .dialog-container {
        background: white;
        border-radius: 8px;
        box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
        max-width: 520px; /* reduced dialog width for a smaller footprint */
        width: 90%;
        max-height: 80vh;
        overflow: hidden;
        padding: 1rem; /* slightly reduced padding */
      }

      /* Single dialog content section (using RDS labels) */
      .dialog-content .dialog-title {
        display: block;
        margin: 0 0 0.5rem 0;
        font-size: 1.125rem;
        font-weight: 600;
        color: #111827;
      }

      .dialog-content .dialog-question {
        display: block;
        margin: 0 0 0.75rem 0;
        color: #374151;
        line-height: 1.5;
      }

      .dialog-question {
        margin: 0;
        color: #374151;
        line-height: 1.4;
        white-space: nowrap; /* prevent wrapping */
        overflow: hidden;
        text-overflow: ellipsis; /* truncate if too long */
      }

      .button-row {
        display: flex;
        justify-content: flex-end; /* place buttons on the right */
        gap: 0.75rem;
        margin-top: 0.75rem;
        align-items: center;
      }

      .button-row rds-button {
        min-width: 100px; /* slightly smaller buttons to fit */
      }

      /* batch list removed from dialog */

      /* Success Toast Styles */
      .success-toast {
        position: fixed;
        top: 20px;
        right: 20px;
        z-index: 1001;
      }

      .toast-content {
        background: #10b981;
        color: white;
        padding: 1rem 1.5rem;
        border-radius: 6px;
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
        display: flex;
        align-items: center;
        gap: 1rem;
      }

      /* Generic toast container for rds-toast elements */
      .toast-container {
        position: fixed;
        top: 1rem;
        right: 1rem;
        z-index: 1100;
        display: flex;
        flex-direction: column;
        gap: 0.5rem;
        align-items: center;
        pointer-events: none; /* allow clicks to pass through except on toast items */
      }

      .toast-item {
        pointer-events: auto;
      }

      .close-button {
        background: none;
        border: none;
        color: white;
        font-size: 1.25rem;
        cursor: pointer;
        padding: 0;
        width: 20px;
        height: 20px;
        display: flex;
        align-items: center;
        justify-content: center;
      }
      /* Position notifications at top-right */
      .notification-top {
        position: fixed;
        top: 20px;
        left: 50%;
        transform: translateX(-50%);
        z-index: 2000;
        max-width: 90%;
        display: block;
        text-align: center;
      }
    `,
  ],
})
export class BatchTableComponent implements OnInit, OnChanges, OnDestroy {
  public onFileSelected(evt: Event): void {
    const input = evt.target as HTMLInputElement;
    const files = Array.from(input?.files ?? []);
    if (files.length === 0) return;
    if (files.length > 60) {
      this.toastService.show(
        'error',
        'Too many files',
        'You can upload a maximum of 60 files.'
      );
      input.value = ''; // clear selected files so user can reselect
      return;
    }
    const uploaderEmail = this.getCurrentUserEmail();

    if (!uploaderEmail) {
      this.toastService.show('error', 'Email missing', 'Could not determine your login email.');
      return;
    }
    const metadata: Record<string, string> = { connector: this.connector || '' };

    this.toastService.show('info', 'Uploading', 'Uploading document...');

    this.batchService.uploadDocument(files, uploaderEmail, metadata)
      .subscribe({
        next: (res: unknown) => {
          const httpEvent = res as HttpEvent<unknown>;
          if (httpEvent.type === HttpEventType.Response) {
            this.toastService.show('success', 'Uploaded', 'Document uploaded successfully');
            this.onRefresh();
          }
        },
        error: (err) => {
          console.error('Upload failed', err);
          this.toastService.show(
            'error',
            'Upload failed',
            err?.message || 'Upload failed'
          );
        }
      });
  }
 private getCurrentUserEmail(): string {
    try {
      const active = this.msalService.instance.getActiveAccount();
      if (!active) return '';
      const claims =
        ((active as { idTokenClaims?: Record<string, unknown> } | null)?.idTokenClaims) || {};
      const candidate =
        claims['email'] ||
        claims['preferred_username'] ||
        claims['upn'] ||
        (Array.isArray(claims['emails']) ? claims['emails'][0] : undefined) ||
        active.username ||
        '';
      return typeof candidate === 'string' && candidate.includes('@') ? candidate : '';
    } catch { return ''; }
  }
  @ViewChild('fileInput') fileInput!: ElementRef<HTMLInputElement>;
  public onUploadClick(evt: Event): void {
    evt.preventDefault();
    try {
      this.fileInput.nativeElement.value = '';
      this.fileInput.nativeElement.click();
    } catch (e) {
      console.error('Failed to open file selector', e);
    }
  }

  @Input() connector!: string;
  @Input() batches: Batch[] = [];
  @Input() loading = false;
  @Output() selectionChange = new EventEmitter<Batch[]>();
  @Output() batchAction = new EventEmitter<BatchAction>();
  @Output() refresh = new EventEmitter<void>();
  @Output() moreOptionsAction = new EventEmitter<'accounts' | 'password'>();

  columns: ColumnConfig[] = [];
  defaultSort?: { field: string; order: 'asc' | 'desc' };
  selection: Batch[] = [];
  showActionFooter = false;
  vcfUploadState: 'idle' | 'progress' | 'completed' | 'failed' = 'idle';
  private vcfUploadStateTimer: ReturnType<typeof setTimeout> | null = null;
  private pendingUploadedFileNames: string[] = [];
  private vcfUploadPollTimer: ReturnType<typeof setTimeout> | null = null;
  private vcfUploadPollInFlight = false;
  private readonly vcfUploadPollIntervalMs = 3000;
  // Document Type filter
  documentTypeFilter: string = '';
  appliedDocumentTypeFilter: string = '';
  availableDocumentTypes: string[] = [];
  uploadedByFilter: string = '';
  appliedUploadedByFilter: string = '';
  availableUploadedBy: string[] = [];

  // More Options state
  showMoreOptions = false;
  pendingAction: 'verify' | 'reprocess' | 'delete' | 'clearOperator' | 'download' | null = null;
  @Input() pageSize = 10;
  // Preserve the initial pageSize so we can reset to it on refresh
  initialPageSize = 10;
  @Input() pageIndex = 0; // zero-based
  @Output() pageChange = new EventEmitter<number>();
  @Input() pageSizeOptions: number[] = [10, 25, 50, 100];
  sortField?: string;
  sortOrder: 'asc' | 'desc' | null = null;

  // Search/Filter properties
  searchText: string = '';
  statusFilter: string = '';
  createdFromDate: string = '';
  createdToDate: string = '';
  // Parsed Date objects for filtering (avoid reparsing on each filter op)
  createdFrom: Date | null = null;
  createdTo: Date | null = null;
  // Applied filters (only used when user clicks Search)
  appliedSearchText: string = '';
  appliedStatusFilter: string = '';
  appliedCreatedFrom: Date | null = null;
  appliedCreatedTo: Date | null = null;
  // Datepicker locale (affects how incoming string values are parsed)
  datePickerLocale: string = 'en-US';
  availableStatuses: string[] = [];

  // Delete dialog properties
  showDeleteDialog = false;
  batchesToDelete: Batch[] = [];
  // Claim (clear operator) confirmation dialog properties
  showClaimDialog = false;
  batchesToClaim: Batch[] = [];

  // Download confirmation when metadata incomplete
  showDownloadConfirmDialog = false;
  pendingDownloadBatches: Batch[] = [];
  downloadIncompleteCount: number = 0;
  downloadConfirmMessage = '';

  // Hold-download dialog state
  showHoldDownloadDialog = false;
  holdOwnerName = '';
  holdOwnerMessage = '';
  holdMessagePrefix = '';

  // Toasts are provided globally by ToastService (rendered in app root via app-toast-outlet)

  // User verification permissions
  currentUser: string = '';

  // State management
  selectedBatchId: string = '';
  private pendingCheckboxRestoration: string[] = [];
  // Inline popover state: holds batchId of open popover (null = none)
  openPopoverBatchId: string | null = null;
  private refreshInProgress = false;
  private refreshToastId: number | null = null;
  // Cache for pages availability per batchId: undefined = unknown, true = has pages, false = empty
  pagesAvailable: { [batchId: string]: boolean | undefined } = {};
  // Guards to prevent re-entrant normalization when the datepicker re-emits normalized values
  private _suppressFromNormalization = false;
  private _suppressToNormalization = false;
  // Snapshot used to restore filter inputs after async row updates (e.g., reprocess)
  private filterSnapshot: {
    searchText: string;
    statusFilter: string;
    documentTypeFilter: string;
    uploadedByFilter: string;
    createdFromDate: string;
    createdToDate: string;
    appliedSearchText: string;
    appliedStatusFilter: string;
    appliedDocumentTypeFilter: string;
    appliedUploadedByFilter: string;
    appliedCreatedFrom: Date | null;
    appliedCreatedTo: Date | null;
  } | null = null;
  // Bound popstate handler so we can add/remove listener reliably
  private _boundPopStateHandler: ((ev: PopStateEvent) => void) | null = null;

  get total(): number {
    return this.filteredBatches?.length || 0;
  }
  get pageCount(): number {
    return this.pageSize > 0 ? Math.ceil(this.total / this.pageSize) : 0;
  }
  get displayedBatches(): Batch[] {
    // Use the sorted list (which applies filtering then any active sort)
    const list = this.sortedBatches;
    const start = this.pageIndex * this.pageSize;
    return list.slice(start, start + this.pageSize);
  }

  get filteredBatches(): Batch[] {
    let data = [...(this.batches || [])];
    // Always sort by queueId desc if no explicit sort is set
    if ((!this.sortField || !this.sortOrder) && data.length > 0 && data[0].queueId !== undefined) {
      data.sort((a, b) => Number(b.queueId) - Number(a.queueId));
    }

    // Apply search text filter (only applied when user clicked Search)
    if (this.appliedSearchText && this.appliedSearchText.trim()) {
      const searchLower = this.appliedSearchText.toLowerCase().trim();
      const searchDigits = searchLower.replace(/\D/g, '');
      const searchTokens = searchLower.split(/\s+/).filter((t) => t.length > 0);

      const normalize = (v: unknown): string => {
        if (v === null || v === undefined) return '';
        try {
          return String(v)
            .toLowerCase()
            .replace(/[.,\/#!$%\^&\*;:{}=\-_`~()\[\]\"']/g, ' ')
            .replace(/\s+/g, ' ')
            .trim();
        } catch (e) {
          return '';
        }
      };

      const tokensMatch = (fieldValue: unknown): boolean => {
        const norm = normalize(fieldValue);
        if (!norm) return false;
        return searchTokens.every((tok) => norm.indexOf(tok) !== -1);
      };

      const formatDate = (dateValue: unknown): string => {
        if (!dateValue) return '';
        try {
          if (typeof dateValue === 'string') {
            const dateTimeRegex = /^\d{2}\/\d{2}\/\d{4} \d{2}:\d{2}$/;
            if (dateTimeRegex.test(dateValue.trim())) {
              return dateValue.trim();
            }
          }
          let date: Date;
          if (dateValue instanceof Date) {
            date = dateValue;
          } else if (typeof dateValue === 'string') {
            date = new Date(dateValue);
          } else if (typeof dateValue === 'number') {
            date = new Date(dateValue);
          } else {
            return String(dateValue);
          }
          if (isNaN(date.getTime())) return String(dateValue);
          const day = date.getDate().toString().padStart(2, '0');
          const month = (date.getMonth() + 1).toString().padStart(2, '0');
          const year = date.getFullYear();
          const hours = date.getHours().toString().padStart(2, '0');
          const minutes = date.getMinutes().toString().padStart(2, '0');
          return `${month}/${day}/${year} ${hours}:${minutes}`;
        } catch (error) {
          return String(dateValue);
        }
      };

      const formatNumber = (value: unknown): string => {
        if (value === null || value === undefined) return '';
        return String(value);
      };

      // Only search values that are present in the visible table columns
      data = data.filter((batch) => {
        for (const col of this.columns || []) {
          try {
            let val = this.getCellValue(batch, col);
            if (col.type === 'date') {
              const formatted = formatDate(val).toLowerCase();
              if (formatted.includes(searchLower)) return true;
              if (searchDigits.length > 0 && formatted.replace(/\D/g, '').includes(searchDigits)) return true;
            } else {
              if (val === null || val === undefined) continue;
              const norm = normalize(val);
              if (norm.includes(searchLower)) return true;
              if (tokensMatch(val)) return true;
              if (formatNumber(val).includes(searchLower)) return true;
            }
          } catch (e) {
            /* ignore individual column errors */
          }
        }
        return false;
      });
    }

    // Apply status filter (applied on Search)
    if (this.appliedStatusFilter && this.appliedStatusFilter.trim()) {
      data = data.filter(
        (batch) => batch.status?.toLowerCase() === this.appliedStatusFilter.toLowerCase(),
      );
    }
    // Apply Document Type filter (applied on Search) — only for PFEU
    if (
      this.isPfeuConnector &&
      this.appliedDocumentTypeFilter &&
      this.appliedDocumentTypeFilter.trim()
    ) {
      const needle = this.appliedDocumentTypeFilter.toLowerCase();
      data = data.filter((batch) => (this.getDocumentType(batch) || '').toLowerCase() === needle);
    }

    if (
      this.isVcfConnector &&
      this.appliedUploadedByFilter &&
      this.appliedUploadedByFilter.trim()
    ) {
      const needle = this.appliedUploadedByFilter.toLowerCase();
      data = data.filter((batch) => this.getUploadedBy(batch).toLowerCase() === needle);
    }

    // Date filter behavior (Upload on column / `datetime`):
    // - If only Upload start date is set: include rows with `datetime` >= start
    // - If only Upload end date is set: include rows with `datetime` <= end (inclusive)
    // - If both set: include rows where `datetime` is between start and end (inclusive)
    // Use parsed Date objects when available (set in onCreatedFrom/ToChange)
    // If the date input strings are empty, ensure parsed dates are cleared so filters are not applied
    // Date filter behavior (Upload on column / `datetime`) — only applied when user clicks Search
    // If only one date provided, treat it as that particular date (00:00 -> 23:59). If both provided, treat as inclusive range.
    if (this.appliedCreatedFrom || this.appliedCreatedTo) {
      const fromDate = this.appliedCreatedFrom ? new Date(this.appliedCreatedFrom.getTime()) : null;
      const toDate = this.appliedCreatedTo ? new Date(this.appliedCreatedTo.getTime()) : null;
      if (fromDate) {
        fromDate.setHours(0, 0, 0, 0);
      }
      if (toDate) {
        toDate.setHours(23, 59, 59, 999);
      }

      // If only one side provided, and the other is null, make both sides equal to that day (filter that particular date)
      if (fromDate && !toDate) {
        const singleEnd = new Date(fromDate.getTime());
        singleEnd.setHours(23, 59, 59, 999);
        data = data.filter((batch) => {
          const uploaded = this.toDisplayDate(batch.datetime, 'datetime');
          if (!uploaded) return false;
          return uploaded >= fromDate && uploaded <= singleEnd;
        });
      } else if (!fromDate && toDate) {
        const singleStart = new Date(toDate.getTime());
        singleStart.setHours(0, 0, 0, 0);
        data = data.filter((batch) => {
          const uploaded = this.toDisplayDate(batch.datetime, 'datetime');
          if (!uploaded) return false;
          return uploaded >= singleStart && uploaded <= toDate;
        });
      } else if (fromDate && toDate) {
        data = data.filter((batch) => {
          const uploaded = this.toDisplayDate(batch.datetime, 'datetime');
          if (!uploaded) return false;
          return uploaded >= fromDate && uploaded <= toDate;
        });
      }
    }

    return data;
  }

  get sortedBatches(): Batch[] {
    const data = this.filteredBatches;
    if (!this.sortField || !this.sortOrder) return data;

    const col = this.columns.find((c) => c.field?.toString() === this.sortField);
    if (!col) return data;

    const orderFactor = this.sortOrder === 'asc' ? 1 : -1;

    const toComparable = (batch: Batch): unknown => {
      if (col.type === 'metadata') {
        const v = this.getMetadataValue(batch, col.metadataField);
        return (v ?? '').toString().toLowerCase();
      }
      const value = this.getCellValue(batch, col);
      if (value == null) return null;
      if (col.type === 'date') {
        const d = this.toDisplayDate(value, col.field?.toString(), col.format);
        return d ? d.getTime() : null;
      }
      if (typeof value === 'number') return value;
      // try numeric parse if numeric-like
      const num = Number(value);
      if (!Number.isNaN(num) && value !== '' && value !== null) return num;
      return value.toString().toLowerCase();
    };

    return data.sort((a, b) => {
      const av = toComparable(a);
      const bv = toComparable(b);
      if (av == null && bv == null) return 0;
      if (av == null) return 1; // nulls last
      if (bv == null) return -1;
      if (av < bv) return -1 * orderFactor;
      if (av > bv) return 1 * orderFactor;
      return 0;
    });
  }

  public isFilterApplied(): boolean {
    return !!(
      (this.appliedSearchText && this.appliedSearchText.toString().trim() !== '') ||
      (this.appliedStatusFilter && this.appliedStatusFilter.toString().trim() !== '') ||
      (this.isPfeuConnector &&
        this.appliedDocumentTypeFilter &&
        this.appliedDocumentTypeFilter.toString().trim() !== '') ||
      (this.isVcfConnector &&
        this.appliedUploadedByFilter &&
        this.appliedUploadedByFilter.toString().trim() !== '') ||
      this.appliedCreatedFrom ||
      this.appliedCreatedTo
    );
  }

  // Parse a date string used by the datepicker (YYYY-MM-DD) into a local Date
  private parseFilterDate(dateStr: string | null): Date | null {
    if (!dateStr) return null;
    const s = dateStr.toString().trim();
    // ISO-style YYYY-MM-DD or YYYY-MM-DD HH:mm[:ss]
    const yMd = s.match(/^(\d{4})-(\d{2})-(\d{2})(?:\s+(\d{1,2}):(\d{2})(?::(\d{1,2}))?)?$/);
    if (yMd) {
      const year = Number(yMd[1]);
      const month = Number(yMd[2]) - 1;
      const day = Number(yMd[3]);
      const hour = yMd[4] ? Number(yMd[4]) : 0;
      const minute = yMd[5] ? Number(yMd[5]) : 0;
      const second = yMd[6] ? Number(yMd[6]) : 0;
      const d = new Date(year, month, day, hour, minute, second);
      return isNaN(d.getTime()) ? null : d;
    }

    // Common slash-separated form: either MM/DD/YYYY or DD/MM/YYYY (may include time)
    const slash = s.match(
      /^(\d{1,2})[\/-](\d{1,2})[\/-](\d{4})(?:\s+(\d{1,2}):(\d{2})(?::(\d{1,2}))?)?$/,
    );
    if (slash) {
      const p1 = Number(slash[1]);
      const p2 = Number(slash[2]);
      const year = Number(slash[3]);
      const hour = slash[4] ? Number(slash[4]) : 0;
      const minute = slash[5] ? Number(slash[5]) : 0;
      const second = slash[6] ? Number(slash[6]) : 0;
      let month: number;
      let day: number;
      // Respect configured datePickerLocale when disambiguating
      if (this.datePickerLocale && this.datePickerLocale.toLowerCase().startsWith('en')) {
        // Treat as MM/DD/YYYY for en locales
        month = p1 - 1;
        day = p2;
      } else {
        // Treat as DD/MM/YYYY for non-en locales
        month = p2 - 1;
        day = p1;
      }
      const d = new Date(year, month, day, hour, minute, second);
      return isNaN(d.getTime()) ? null : d;
    }

    // Fallback to toDate which has broader heuristics (dd/MM/YYYY, ISO with T/Z, etc.)
    return this.toDate(s);
  }

  public selectionOnPageCount(): number {
    const ids = new Set(this.displayedBatches.map((b) => b.batchId));
    return this.selection.filter((s) => ids.has(s.batchId)).length;
  }

  public onPageChange(event: Event | CustomEvent<unknown> | Record<string, unknown>): void {
    // Support multiple possible event payload shapes from the web component
    let newPageIndex: number | null = null;
    let newPageSize: number | null = null;

    // RDS emits CustomEvent with detail likely containing page/perPage
    const eventRecord = event as Record<string, unknown>;
    const detail = eventRecord['detail'] ?? eventRecord;
    if (detail) {
      const d = detail;
      if (typeof d === 'number' || typeof d === 'string') {
        const n = Number(d);
        if (!Number.isNaN(n)) newPageIndex = n - 1; // 1-based
      } else if (typeof d === 'object') {
        const obj = d as Record<string, unknown>;
        const pageVals = [obj['page'], obj['currentPage'], obj['Page'], obj['PAGE'], obj['pageIndex']];
        for (const v of pageVals) {
          if (v != null) {
            const n = Number(v);
            if (!Number.isNaN(n)) {
              newPageIndex = v === obj['pageIndex'] ? n : n - 1; // pageIndex is 0-based, others 1-based
              break;
            }
          }
        }
        const sizeVals = [obj['perPage'], obj['per-page'], obj['size'], obj['pageSize'], obj['per_page']];
        for (const s of sizeVals) {
          if (s != null) {
            const n = Number(s);
            if (!Number.isNaN(n) && n > 0) {
              newPageSize = n;
              break;
            }
          }
        }
      }
    }

    if (newPageSize != null && newPageSize > 0) {
      this.pageSize = newPageSize;
    }
    // Fallback: try to infer from target attributes if present
    if (newPageIndex == null || newPageIndex < 0) {
      const target =
        ((event as Event).target as HTMLElement | null) ??
        ((event as Event).currentTarget as HTMLElement | null);
      const attrPage = target?.getAttribute?.('page') ?? target?.getAttribute?.('current-page');
      const attrPerPage = target?.getAttribute?.('per-page') ?? target?.getAttribute?.('page-size');
      if (attrPerPage) {
        const n = Number(attrPerPage);
        if (!Number.isNaN(n) && n > 0) this.pageSize = n;
      }
      if (attrPage) {
        const p = Number(attrPage);
        if (!Number.isNaN(p)) newPageIndex = Math.max(0, p - 1);
      }
    }
    if (newPageIndex != null) {
      this.pageIndex = Math.min(Math.max(0, newPageIndex), Math.max(0, this.pageCount - 1));
      this.pageChange.emit(this.pageIndex);
    }
  }

  public onPageSizeChange(raw: unknown): void {
    const size = Number(raw);
    if (!Number.isNaN(size) && size > 0) {
      this.pageSize = size;
      this.pageIndex = 0; // reset to first page
      this.pageChange.emit(this.pageIndex);
    }
  }

  constructor(
    private configService: ConnectorConfigService,
    private batchService: BatchService,
    private msalService: MsalService,
    private batchStateService: BatchStateService,
    private cd: ChangeDetectorRef,
    private router: Router,
    private toastService: ToastService,
    private nameFormatService: NameFormatService,
  ) {}

  // Handler called when UploadBatchComponent emits list of uploaded filenames
  public onUploadCompleted(filenames: string[] | null | undefined): void {
    const name = localStorage.getItem('lastUploadedBy') || '';
    if (!filenames || filenames.length === 0) {
      // fallback to refresh
      this.onRefresh();
      return;
    }

    if (this.isVcfConnector) {
      this.pendingUploadedFileNames = filenames
        .map((f) => (f || '').toString().trim().toLowerCase())
        .filter((f) => !!f);
      this.vcfUploadState = 'progress';
      this.startVcfUploadPolling();
    }

    // Update local batches where batchName matches uploaded filename
    const uploadedSet = new Set(filenames.map(f => f.toString()));
    let touched = false;
    this.batches = (this.batches || []).map(b => {
      const bn = (b.batchName || '').toString();
      // match exact name or if batchName contains the filename
      if (uploadedSet.has(bn) || filenames.some(fn => bn.indexOf(fn) !== -1)) {
        touched = true;
        return { ...b, uploadedBy: name };
      }
      return b;
    });

    if (touched) {
      if (this.isVcfConnector) {
        this.extractAvailableUploadedBy();
        this.ensureUploadedByFilterOption(name);
      }
      try { this.cd.detectChanges(); } catch (e) {}
    } else {
      // if we didn't find matches, trigger a refresh to ensure server state
      this.onRefresh();
    }
  }

  /** Normalize connector tokens so visibility checks are stable across routes and formats */
  private canonicalizeConnector(raw: string): string {
    if (!raw) return '';
    const up = raw.toString().toUpperCase();
    const stripped = up.replace(/[^A-Z0-9]/g, '');

    // Common mappings (mirror verify.ts)
    if (stripped === 'AGDESK' || up === 'AG_DESK' || up === 'AG-DESK') return 'AG_DESK';
    if (stripped.includes('PFEU') || (stripped.startsWith('PF') && stripped.includes('EU')))
      return 'PFEU';
    if (
      stripped.includes('PFNA') ||
      (stripped.startsWith('PF') && (stripped.includes('NA') || stripped.includes('NORTH')))
    )
      return 'PFNA';
    if(stripped == 'VCF') return 'VCF';
    // Fallback: return cleaned uppercase token if present in configs, else original uppercase
    return stripped || up;
  }

  /** True when the connector is Project Finance EU */
  get isPfeuConnector(): boolean {
    const conn = this.canonicalizeConnector(this.connector || '');
    return conn === 'PFEU' || conn == 'VCF';
  }

  get isVcfConnector(): boolean {
  const conn = this.canonicalizeConnector(this.connector || '');
  return conn === 'VCF';
}

  get isProjectFinanceConnector(): boolean {
    const conn = this.canonicalizeConnector(this.connector || '');
    return conn.startsWith('PF');
  }

  get showsUploadProgressBanner(): boolean {
    return this.isVcfConnector || this.isProjectFinanceConnector;
  }

  public ngOnInit(): void {
    const config = this.configService.getConnectorConfig(this.connector);
    if (config) {
      this.columns = config.columns.filter((col) => col.visible !== false);
      // Use connector config's defaultSort
      this.defaultSort = config.defaultSort;
      this.sortField = this.defaultSort?.field;
      this.sortOrder = this.defaultSort?.order ?? null;
    }

    // Get current user for verification permissions
    this.getCurrentUser();

    // Capture initial page size so refresh can restore it
    this.initialPageSize = this.pageSize || 10;

    // Restore state if available
    this.restoreState();

    // If a deferred toast was set by a previous screen (e.g. verify save/submit),
    // show it now and remove it from persisted state so it doesn't repeat.
    try {
      const currentState = this.batchStateService.getState() as BatchListState & {
        deferredToast?: {
          type: ToastType;
          title?: string;
          message: string;
          duration?: number;
        };
      };
      if (currentState?.deferredToast) {
        const t = currentState.deferredToast;
        this.toastService.show(t.type, t.title || '', t.message, t.duration);
        // Clear deferred toast and persist cleared state
        const cleared = { ...currentState } as BatchListState & {
          deferredToast?: {
            type: ToastType;
            title?: string;
            message: string;
            duration?: number;
          };
        };
        delete cleared.deferredToast;
        this.batchStateService.saveState(cleared as BatchListState);
      }
    } catch (e) {
      // ignore
    }

    // Extract unique statuses from batches for the filter dropdown
    this.extractAvailableStatuses();
    this.extractAvailableDocumentTypes();
    this.extractAvailableUploadedBy();

    // Install a popstate listener to detect browser back/forward navigation
    // When the user navigates via browser history to the batch screen we must
    // clear any checkbox selections and refresh the data so previously
    // checked batches are not preserved across history traversal.
    this._boundPopStateHandler = (_ev: PopStateEvent) => {
      // Clear any UI selection
      this.clearSelections();
      // Clear persisted checked batches in saved state
      try {
        const current = this.batchStateService.getState();
        this.batchStateService.saveState({ ...current, checkedBatches: [] });
      } catch (e) {
        // ignore
      }
      // Ask parent to refresh/reload the latest batches
      try {
        this.refresh.emit();
      } catch (e) {
        /* ignore */
      }
    };
    window.addEventListener('popstate', this._boundPopStateHandler);

    // If this navigation itself was triggered by popstate, proactively clear
    // selection and refresh (some Angular navigations expose this via
    // getCurrentNavigation()).
    try {
      const nav = this.router.getCurrentNavigation?.();
      if (
        nav &&
        (nav.trigger === 'popstate' ||
          (nav as { navigationTrigger?: string }).navigationTrigger === 'popstate')
      ) {
        // Clear persisted checked batches and UI selection
        this.clearSelections();
        const current = this.batchStateService.getState();
        this.batchStateService.saveState({ ...current, checkedBatches: [] });
        this.refresh.emit();
      }
    } catch (e) {
      // ignore
    }
  }

  public ngOnDestroy(): void {
    if (this.vcfUploadStateTimer) {
      clearTimeout(this.vcfUploadStateTimer);
      this.vcfUploadStateTimer = null;
    }

    if (this.vcfUploadPollTimer) {
      clearTimeout(this.vcfUploadPollTimer);
      this.vcfUploadPollTimer = null;
    }

    if (this._boundPopStateHandler) {
      window.removeEventListener('popstate', this._boundPopStateHandler);
      this._boundPopStateHandler = null;
    }
  }

  public onUploadStatusChange(state: 'progress' | 'completed' | 'failed'): void {
    if (this.vcfUploadStateTimer) {
      clearTimeout(this.vcfUploadStateTimer);
      this.vcfUploadStateTimer = null;
    }

    if (state === 'progress') {
      this.vcfUploadState = 'progress';
      return;
    }

    if (state === 'completed') {
      // For VCF keep progress until poll confirms ingestion; PF can show completed immediately.
      if (this.isVcfConnector) {
        this.vcfUploadState = 'progress';
        return;
      }

      this.vcfUploadState = 'completed';
      this.vcfUploadStateTimer = setTimeout(() => {
        this.vcfUploadState = 'idle';
        this.vcfUploadStateTimer = null;
        try {
          this.cd.detectChanges();
        } catch {
          /* ignore */
        }
      }, 4000);
      return;
    }

    if (this.isVcfConnector && this.vcfUploadPollTimer) {
      clearTimeout(this.vcfUploadPollTimer);
      this.vcfUploadPollTimer = null;
    }

    if (this.isVcfConnector) {
      this.pendingUploadedFileNames = [];
    }
    this.vcfUploadState = 'idle';
  }

  public ngOnChanges(changes: SimpleChanges): void {
    // 1) When the batches input changes (new data arrived)
    if (changes['batches'] && !changes['batches'].firstChange) {
      this.checkPendingUploadedBatches(this.batches || []);

      // Rebuild Status list for the filter
      this.extractAvailableStatuses();
      if (this.statusFilter && this.availableStatuses.indexOf(this.statusFilter) === -1) {
        this.statusFilter = '';
        this.appliedStatusFilter = '';
      }

      // Rebuild Document Type list only for PFEU; otherwise clear it
      if (this.isPfeuConnector) {
        this.extractAvailableDocumentTypes();
        this.extractAvailableUploadedBy();
      } else {
        this.availableDocumentTypes = [];
        this.documentTypeFilter = '';
        this.appliedDocumentTypeFilter = '';
        this.availableUploadedBy = [];
        this.uploadedByFilter = '';
        this.appliedUploadedByFilter = '';
      }

      // --- Existing restoration logic (unchanged) ---
      // Restore checkbox selections if there are any pending
      if (this.pendingCheckboxRestoration.length > 0) {
        this.restoreCheckboxSelections(this.pendingCheckboxRestoration);
        this.pendingCheckboxRestoration = [];
      }

      // If we have a selectedBatchId persisted in state (from verify screen)
      // but no checkbox selections, ensure the table shows the page containing
      // that batch so the user returns to the same page and the row is visible.
      if (this.selectedBatchId && (!this.selection || this.selection.length === 0)) {
        const sorted = this.sortedBatches;
        const idx = sorted.findIndex((b) => b.batchId === this.selectedBatchId);
        if (idx >= 0 && this.pageSize > 0) {
          const newPageIndex = Math.floor(idx / this.pageSize);
          if (newPageIndex !== this.pageIndex) {
            this.pageIndex = Math.min(Math.max(0, newPageIndex), Math.max(0, this.pageCount - 1));
            try {
              this.pageChange.emit(this.pageIndex);
            } catch (e) {
              /* ignore */
            }
          }
        }
      }
    }

    // 2) When the connector changes (user switches application)
    if (changes['connector'] && !changes['connector'].firstChange) {
      // If switching specifically between PFEU and AG_DESK, reset any user-entered
      // search/filter inputs so stale values don't carry over between applications.
      try {
        const prev = this.canonicalizeConnector(changes['connector'].previousValue || '');
        const curr = this.canonicalizeConnector(changes['connector'].currentValue || this.connector || '');
        if (
          (prev === 'PFEU' && curr === 'AG_DESK') ||
          (prev === 'AG_DESK' && curr === 'PFEU')
        ) {
          // Clear UI filters and applied filters immediately
          this.clearFilters();
          // Also clear persisted state to avoid restore collisions
          try {
            this.batchStateService.clearState();
          } catch (e) {
            /* ignore */
          }
        }
      } catch (e) {
        /* ignore any canonicalization issues and continue */
      }
      // Refresh table config for the new connector
      const config = this.configService.getConnectorConfig(this.connector);
      if (config) {
        this.columns = config.columns.filter((col) => col.visible !== false);
        this.defaultSort = config.defaultSort;
        this.sortField = this.defaultSort?.field;
        this.sortOrder = this.defaultSort?.order ?? null;
      }

      // Rebuild Status list for the filter (statuses can differ across connectors)
      this.extractAvailableStatuses();
      if (this.statusFilter && this.availableStatuses.indexOf(this.statusFilter) === -1) {
        this.statusFilter = '';
        this.appliedStatusFilter = '';
      }

      // PFEU-only behavior for Document Type filter
      if (this.isPfeuConnector) {
        // Arriving at / switching to PFEU → build the list from current batches
        this.extractAvailableDocumentTypes();
        this.extractAvailableUploadedBy();
      } else {
        // Leaving PFEU → clear everything related to doc type to avoid stale UI/state
        this.availableDocumentTypes = [];
        this.documentTypeFilter = '';
        this.appliedDocumentTypeFilter = '';
        this.availableUploadedBy = [];
        this.uploadedByFilter = '';
        this.appliedUploadedByFilter = '';
      }

      // Optional: whenever connector changes, reset to first page to avoid empty pages
      // and keep pagination consistent. If you prefer to preserve page, comment out.
      this.pageIndex = 0;
      try {
        this.pageChange.emit(this.pageIndex);
      } catch (e) {
        /* ignore */
      }
    }
  }

  private normalizeUploadedName(name: string): string {
    return (name || '').toString().trim().toLowerCase();
  }

  private stripPdfExtension(name: string): string {
    return this.normalizeUploadedName(name).replace(/\.pdf$/i, '');
  }

  private areAllPendingUploadedBatchesInList(data: Batch[]): boolean {
    if (!this.pendingUploadedFileNames || this.pendingUploadedFileNames.length === 0) {
      return false;
    }

    const batchNames = (data || [])
      .map((b) => this.normalizeUploadedName(b.batchName || ''))
      .filter((n) => !!n);

    return this.pendingUploadedFileNames.every((uploaded) => {
      const normalized = this.normalizeUploadedName(uploaded);
      const withoutExt = this.stripPdfExtension(uploaded);
      return batchNames.some(
        (bn) =>
          bn === normalized ||
          bn.includes(normalized) ||
          bn === withoutExt ||
          bn.includes(withoutExt),
      );
    });
  }

  private checkPendingUploadedBatches(data: Batch[]): void {
    if (!this.isVcfConnector) return;
    if (!this.pendingUploadedFileNames || this.pendingUploadedFileNames.length === 0) return;

    if (this.areAllPendingUploadedBatchesInList(data || [])) {
      this.pendingUploadedFileNames = [];
      this.vcfUploadState = 'completed';

      if (this.vcfUploadPollTimer) {
        clearTimeout(this.vcfUploadPollTimer);
        this.vcfUploadPollTimer = null;
      }

      this.vcfUploadStateTimer = setTimeout(() => {
        this.vcfUploadState = 'idle';
        this.vcfUploadStateTimer = null;
        try {
          this.cd.detectChanges();
        } catch {
          /* ignore */
        }
      }, 4000);
    }
  }

  private startVcfUploadPolling(): void {
    if (!this.isVcfConnector) return;
    if (!this.pendingUploadedFileNames || this.pendingUploadedFileNames.length === 0) return;

    if (this.vcfUploadPollTimer) {
      clearTimeout(this.vcfUploadPollTimer);
      this.vcfUploadPollTimer = null;
    }

    const poll = () => {
      if (!this.isVcfConnector) return;
      if (!this.pendingUploadedFileNames || this.pendingUploadedFileNames.length === 0) return;
      if (this.vcfUploadPollInFlight) {
        this.vcfUploadPollTimer = setTimeout(poll, this.vcfUploadPollIntervalMs);
        return;
      }

      this.vcfUploadPollInFlight = true;
      this.batchService.getAllBatches(this.connector).subscribe({
        next: (data: Batch[]) => {
          this.batches = data || [];
          this.batchService.batches = this.batches;
          this.extractAvailableStatuses();
          this.extractAvailableDocumentTypes();
          this.extractAvailableUploadedBy();
          this.checkPendingUploadedBatches(this.batches);
          this.vcfUploadPollInFlight = false;

          if (this.pendingUploadedFileNames.length > 0) {
            this.vcfUploadPollTimer = setTimeout(poll, this.vcfUploadPollIntervalMs);
          }
        },
        error: () => {
          this.vcfUploadPollInFlight = false;
          if (this.pendingUploadedFileNames.length > 0) {
            this.vcfUploadPollTimer = setTimeout(poll, this.vcfUploadPollIntervalMs);
          }
        },
      });
    };

    this.vcfUploadPollTimer = setTimeout(poll, 0);
  }

  public extractAvailableStatuses(): void {
    const statusSet = new Set<string>();
    this.batches.forEach((batch) => {
      if (batch.status && batch.status.trim()) {
        statusSet.add(batch.status);
      }
    });
    this.availableStatuses = Array.from(statusSet).sort();
  }

  public onFilterChange(): void {
    // Reset to first page when filters change
    this.pageIndex = 0;
    this.pageChange.emit(this.pageIndex);
    // Ensure pagination and bindings update immediately after filter changes
    setTimeout(() => {
      try {
        this.cd.detectChanges();
      } catch (e) {
        /* ignore */
      }
    }, 0);
  }

  public onSearchChange(searchValue: string): void {
    this.searchText = searchValue;
    // Apply live search without requiring the user to click the Search button.
    // Update the applied search text and refresh the filtered list immediately
    // (this avoids running full date validation which is only performed on explicit Search).
    this.appliedSearchText = searchValue;
    this.pageIndex = 0;
    this.onFilterChange();
  }

  public onSearchKeydown(ev: Event | KeyboardEvent | CustomEvent<unknown> | Record<string, unknown>): void {
    // Try to detect Enter across shapes (KeyboardEvent or CustomEvent.detail)
    const eventRecord = ev as Record<string, unknown>;
    const detail = (eventRecord['detail'] as Record<string, unknown> | undefined) ?? eventRecord;
    const keyboardEvent = ev as KeyboardEvent;
    const keyCode = (eventRecord['keyCode'] as number | undefined) ?? undefined;
    const key =
      (detail['key'] as string | undefined) ??
      keyboardEvent.key ??
      (typeof keyCode === 'number' ? String(keyCode) : null);
    if (key === 'Enter' || key === 'NumpadEnter' || key === '13') {
      // If event detail contains the current input value, sync it first
      const detailTarget = detail['target'] as { value?: string } | undefined;
      const val = (detail['value'] as string | undefined) ?? detailTarget?.value ?? null;
      if (val != null) this.searchText = val;
      this.applyFilters();
    }
  }

  public onRdsSearchButtonClick(ev: Event | CustomEvent<unknown> | Record<string, unknown>): void {
    const eventRecord = ev as Record<string, unknown>;
    const detail = eventRecord['detail'] as Record<string, unknown> | undefined;
    const target = eventRecord['target'] as { value?: string } | undefined;
    const val =
      (detail?.['value'] as string | undefined) ??
      (typeof detail === 'string' ? detail : undefined) ??
      target?.value ??
      this.searchText;
    if (val != null) this.searchText = val;
    this.applyFilters();
  }

  public onStatusChange(statusValue: string): void {
    this.statusFilter = statusValue;
  }

  private getDocumentType(batch: Batch): string {
    // Prefer a direct property if it exists
    const direct = (batch as Batch & { documentType?: string }).documentType;
    if (direct && typeof direct === 'string') return direct;

    // Fallback to metadata if used
    return (
      this.getMetadataValue(batch, 'DocumentType') ||
      this.getMetadataValue(batch, 'documentType') ||
      this.getMetadataValue(batch, 'docType') ||
      ''
    );
  }

  private isAllowedVcfDocumentType(value: string): boolean {
    const normalized = (value || '').toString().trim().toUpperCase();
    return normalized === 'SEA WAYBILL' || normalized === 'BILL OF LADING';
  }

  private getUploadedBy(batch: Batch): string {
    const uploadedRaw = (batch?.uploadedBy || batch?.senderAddress || '').toString();
    return this.nameFormatService.formatName(uploadedRaw).trim();
  }

  public extractAvailableDocumentTypes(): void {
    if (!this.isPfeuConnector) {
      this.availableDocumentTypes = [];
      return;
    }
    const set = new Set<string>();
    (this.batches || []).forEach((batch) => {
      const dt = (this.getDocumentType(batch) || '').trim();
      if (!dt) return;
      if (this.isVcfConnector && !this.isAllowedVcfDocumentType(dt)) return;

      // For VCF, canonicalize known document type variants to a standard label
      let key = dt;
      if (this.isVcfConnector) {
        const compact = dt.replace(/\s+/g, '').toUpperCase();
        if (compact === 'SEAWAYBILL') key = 'SEA WAYBILL';
        else if (compact === 'BILLOFLADING') key = 'BILL OF LADING';
        else key = dt.trim();
      }

      set.add(key);
    });
    this.availableDocumentTypes = Array.from(set).sort();
  }

  public onDocumentTypeChange(value: string): void {
    this.documentTypeFilter = value;
  }

  public extractAvailableUploadedBy(): void {
    if (!this.isVcfConnector) {
      this.availableUploadedBy = [];
      return;
    }
    const set = new Set<string>();
    (this.batches || []).forEach((batch) => {
      const uploader = this.getUploadedBy(batch);
      if (uploader) set.add(uploader);
    });
    this.availableUploadedBy = Array.from(set).sort();
  }

  private ensureUploadedByFilterOption(name: string): void {
    if (!this.isVcfConnector || !name) return;
    const normalized = this.nameFormatService.formatName(name).trim();
    if (!normalized) return;
    if (!this.availableUploadedBy.includes(normalized)) {
      this.availableUploadedBy = [...this.availableUploadedBy, normalized].sort();
    }
    if (this.uploadedByFilter === '' && this.appliedUploadedByFilter === '') {
      this.uploadedByFilter = normalized;
      this.appliedUploadedByFilter = normalized;
    }
  }

  public onUploadedByChange(value: string): void {
    this.uploadedByFilter = value;
  }

  public onCreatedFromChange(dateValue: string | Date | null | undefined): void {
    // Normalize and display the picker value as MM/DD/YYYY while storing a Date for filtering
    if (this._suppressFromNormalization) {
      // Reset the guard and skip further normalization to avoid loops
      this._suppressFromNormalization = false;
      return;
    }
    // Explicit clear from input should reset the value.
    // Ignore transient null/undefined emissions that can happen on blur/re-render.
    if (dateValue === '') {
      this.createdFromDate = '';
      this.createdFrom = null;
      return;
    }
    if (dateValue === null || dateValue === undefined) {
      return;
    }

    // If the datepicker emitted a Date object, use it directly
    if ((dateValue as unknown) instanceof Date) {
      const d = dateValue as unknown as Date;
      if (isNaN(d.getTime())) {
        // invalid date -> clear
        this.createdFromDate = '';
        this.createdFrom = null;
        return;
      }
      const month = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      const year = String(d.getFullYear());
      this.createdFromDate = `${month}/${day}/${year}`;
      this.createdFrom = new Date(d.getFullYear(), d.getMonth(), d.getDate());
      this._suppressFromNormalization = true;
      setTimeout(() => {
        this.createdFromDate = this.createdFromDate;
      }, 0);
      return;
    }
    if (typeof dateValue === 'string') {
      // Accept typed values (slashes or hyphens) and attempt to normalize without showing an error yet.
      const mmddyyyyHyphen = dateValue.match(/^(\d{2})-(\d{2})-(\d{4})$/);
      if (mmddyyyyHyphen) {
        dateValue = `${mmddyyyyHyphen[1]}/${mmddyyyyHyphen[2]}/${mmddyyyyHyphen[3]}`;
      }
      const dmY = dateValue.match(/^(\d{2})[-\/](\d{2})[-\/](\d{4})$/);
      const yMd = dateValue.match(/^(\d{4})[-\/](\d{2})[-\/](\d{2})$/);
      if (yMd) {
        const year = yMd[1];
        const month = yMd[2];
        const day = yMd[3];
        this.createdFromDate = `${month.padStart(2, '0')}/${day.padStart(2, '0')}/${year}`;
        this.createdFrom = new Date(Number(year), Number(month) - 1, Number(day));
        this._suppressFromNormalization = true;
        setTimeout(() => {
          this.createdFromDate = this.createdFromDate;
        }, 0);
      } else if (dmY) {
        let day = dmY[1];
        let month = dmY[2];
        const year = dmY[3];
        if (this.datePickerLocale && this.datePickerLocale.toLowerCase() === 'en-us') {
          // treat as MM/DD/YYYY
          month = dmY[1];
          day = dmY[2];
        }
        this.createdFromDate = `${month.padStart(2, '0')}/${day.padStart(2, '0')}/${year}`;
        this.createdFrom = new Date(Number(year), Number(month) - 1, Number(day));
        this._suppressFromNormalization = true;
        setTimeout(() => {
          this.createdFromDate = this.createdFromDate;
        }, 0);
      } else {
        // Fallback: keep value and attempt to parse; don't show notification here
        this.createdFromDate = dateValue;
        this.createdFrom = this.parseFilterDate(this.createdFromDate);
      }
    } else {
      this.createdFromDate = String(dateValue ?? '');
      this.createdFrom = this.parseFilterDate(this.createdFromDate);
      this._suppressFromNormalization = true;
      setTimeout(() => {
        this.createdFromDate = this.createdFromDate;
      }, 0);
    }
  }

  public onCreatedToChange(dateValue: string | Date | null | undefined): void {
    // Normalize and display the picker value as MM/DD/YYYY while storing a Date for filtering
    if (this._suppressToNormalization) {
      this._suppressToNormalization = false;
      return;
    }
    // Explicit clear from input should reset the value.
    // Ignore transient null/undefined emissions that can happen on blur/re-render.
    if (dateValue === '') {
      this.createdToDate = '';
      this.createdTo = null;
      return;
    }
    if (dateValue === null || dateValue === undefined) {
      return;
    }

    // If the datepicker emitted a Date object, use it directly
    if ((dateValue as unknown) instanceof Date) {
      const d = dateValue as unknown as Date;
      if (isNaN(d.getTime())) {
        this.createdToDate = '';
        this.createdTo = null;
        return;
      }
      const month = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      const year = String(d.getFullYear());
      this.createdToDate = `${month}/${day}/${year}`;
      this.createdTo = new Date(d.getFullYear(), d.getMonth(), d.getDate());
      this._suppressToNormalization = true;
      setTimeout(() => {
        this.createdToDate = this.createdToDate;
      }, 0);
      return;
    }
    if (typeof dateValue === 'string') {
      // Accept typed values (slashes or hyphens) and attempt to normalize without showing an error yet.
      const mmddyyyyHyphen = dateValue.match(/^(\d{2})-(\d{2})-(\d{4})$/);
      if (mmddyyyyHyphen) {
        dateValue = `${mmddyyyyHyphen[1]}/${mmddyyyyHyphen[2]}/${mmddyyyyHyphen[3]}`;
      }
      const dmY = dateValue.match(/^(\d{2})[-\/](\d{2})[-\/](\d{4})$/);
      const yMd = dateValue.match(/^(\d{4})[-\/](\d{2})[-\/](\d{2})$/);
      if (yMd) {
        const year = yMd[1];
        const month = yMd[2];
        const day = yMd[3];
        this.createdToDate = `${month.padStart(2, '0')}/${day.padStart(2, '0')}/${year}`;
        this.createdTo = new Date(Number(year), Number(month) - 1, Number(day));
        this._suppressToNormalization = true;
        setTimeout(() => {
          this.createdToDate = this.createdToDate;
        }, 0);
      } else if (dmY) {
        let day = dmY[1];
        let month = dmY[2];
        const year = dmY[3];
        if (this.datePickerLocale && this.datePickerLocale.toLowerCase() === 'en-us') {
          month = dmY[1];
          day = dmY[2];
        }
        this.createdToDate = `${month.padStart(2, '0')}/${day.padStart(2, '0')}/${year}`;
        this.createdTo = new Date(Number(year), Number(month) - 1, Number(day));
        this._suppressToNormalization = true;
        setTimeout(() => {
          this.createdToDate = this.createdToDate;
        }, 0);
      } else {
        // Fallback: normalize any parseable date string to MM/DD/YYYY.
        // Some picker emissions (including "today") can arrive in alternate formats.
        const parsed = this.parseFilterDate(dateValue);
        if (parsed) {
          const month = String(parsed.getMonth() + 1).padStart(2, '0');
          const day = String(parsed.getDate()).padStart(2, '0');
          const year = String(parsed.getFullYear());
          this.createdToDate = `${month}/${day}/${year}`;
          this.createdTo = new Date(parsed.getFullYear(), parsed.getMonth(), parsed.getDate());
          this._suppressToNormalization = true;
          setTimeout(() => {
            this.createdToDate = this.createdToDate;
          }, 0);
        } else {
          // Keep the typed value when parsing fails; Search validation will handle it.
          this.createdToDate = dateValue;
          this.createdTo = null;
        }
      }
    } else {
      // Keep behavior consistent with Upload start date handler for non-string payloads
      this.createdToDate = String(dateValue ?? '');
      this.createdTo = this.parseFilterDate(this.createdToDate);
      this._suppressToNormalization = true;
      setTimeout(() => {
        this.createdToDate = this.createdToDate;
      }, 0);
    }
  }

  private captureFilterSnapshot(): void {
    this.filterSnapshot = {
      searchText: this.searchText,
      statusFilter: this.statusFilter,
      documentTypeFilter: this.documentTypeFilter,
      uploadedByFilter: this.uploadedByFilter,
      createdFromDate: this.createdFromDate,
      createdToDate: this.createdToDate,
      appliedSearchText: this.appliedSearchText,
      appliedStatusFilter: this.appliedStatusFilter,
      appliedDocumentTypeFilter: this.appliedDocumentTypeFilter,
      appliedUploadedByFilter: this.appliedUploadedByFilter,
      appliedCreatedFrom: this.appliedCreatedFrom ? new Date(this.appliedCreatedFrom) : null,
      appliedCreatedTo: this.appliedCreatedTo ? new Date(this.appliedCreatedTo) : null,
    };
  }

  private restoreFilterSnapshot(): void {
    if (!this.filterSnapshot) return;
    const s = this.filterSnapshot;
    this.searchText = s.searchText;
    this.statusFilter = s.statusFilter;
    this.documentTypeFilter = s.documentTypeFilter;
    this.uploadedByFilter = s.uploadedByFilter;
    this.createdFromDate = s.createdFromDate;
    this.createdToDate = s.createdToDate;
    this.appliedSearchText = s.appliedSearchText;
    this.appliedStatusFilter = s.appliedStatusFilter;
    this.appliedDocumentTypeFilter = s.appliedDocumentTypeFilter;
    this.appliedUploadedByFilter = s.appliedUploadedByFilter;
    this.appliedCreatedFrom = s.appliedCreatedFrom ? new Date(s.appliedCreatedFrom) : null;
    this.appliedCreatedTo = s.appliedCreatedTo ? new Date(s.appliedCreatedTo) : null;
    this.createdFrom = this.parseFilterDate(this.createdFromDate) || this.appliedCreatedFrom;
    this.createdTo = this.parseFilterDate(this.createdToDate) || this.appliedCreatedTo;
    this.filterSnapshot = null;
    try {
      this.cd.detectChanges();
    } catch (e) {
      /* ignore */
    }
  }

  public clearFilters(): void {
    // Clear any active selection/footer state along with filters.
    this.clearSelections();
    this.pendingAction = null;
    this.openPopoverBatchId = null;
    this.selectedBatchId = '';
    this.filterSnapshot = null;

    this.searchText = '';
    this.statusFilter = '';
    this.documentTypeFilter = '';
    this.uploadedByFilter = '';
    this.createdFromDate = '';
    this.createdToDate = '';
    // Clear applied filters as well and refresh
    this.appliedSearchText = '';
    this.appliedStatusFilter = '';
    this.appliedDocumentTypeFilter = '';
    this.appliedUploadedByFilter = '';
    this.appliedCreatedFrom = null;
    this.appliedCreatedTo = null;
    this.createdFrom = null;
    this.createdTo = null;
    // Reset pagination when clearing filters
    this.pageSize = this.initialPageSize || 10;
    this.pageIndex = 0;
    try {
      this.pageChange.emit(this.pageIndex);
    } catch (e) {
      /* ignore */
    }

    // Keep persisted list state in sync with the cleared UI state.
    try {
      const current = this.batchStateService.getState();
      this.batchStateService.saveState({
        ...current,
        pageIndex: this.pageIndex,
        pageSize: this.pageSize,
        searchText: '',
        statusFilter: '',
        documentTypeFilter: '',
        uploadedByFilter: '',
        createdFromDate: '',
        createdToDate: '',
        selectedBatch: '',
        checkedBatches: [],
      });
    } catch (e) {
      /* ignore */
    }

    this.onFilterChange();
  }

  public applyFilters(): void {
    // Validate date inputs only when the user clicks Search.
    // If one date is provided, require both. Also enforce that start <= end.
    const invalidFields: string[] = [];
    const isTypedValid = (dateStr: string | null): boolean => {
      if (dateStr === '' || dateStr === null || dateStr === undefined) return true; // empty allowed
      return /^\d{2}\/\d{2}\/\d{4}$/.test(dateStr || '');
    };

    const fromTyped =
      this.createdFromDate !== '' &&
      this.createdFromDate !== null &&
      this.createdFromDate !== undefined;
    const toTyped =
      this.createdToDate !== '' && this.createdToDate !== null && this.createdToDate !== undefined;

    // If either date is typed (non-empty) require both to be present
    if ((fromTyped && !toTyped) || (!fromTyped && toTyped)) {
      this.toastService.show(
        'error',
        'Date range required',
        'Please provide both Upload start date and Upload end date',
      );
      return;
    }

    // Validate formats when both provided and parse them at Search time
    let parsedFrom: Date | null = null;
    let parsedTo: Date | null = null;
    if (fromTyped && toTyped) {
      if (!isTypedValid(this.createdFromDate as string | null))
        invalidFields.push('Upload start date');
      if (!isTypedValid(this.createdToDate as string | null)) invalidFields.push('Upload end date');

      if (invalidFields.length > 0) {
        const message = `${invalidFields.join(' and ')} must be in mm/dd/yyyy format`;
        this.toastService.show('error', 'Invalid date', message);
        return;
      }

      // Parse the typed strings now (do not rely on prior onChange parsing)
      parsedFrom = this.parseFilterDate(this.createdFromDate as string | null);
      parsedTo = this.parseFilterDate(this.createdToDate as string | null);

      if (!parsedFrom || !parsedTo) {
        this.toastService.show('error', 'Invalid date', 'Unable to parse provided dates');
        return;
      }

      const fromTime = parsedFrom.getTime();
      const toTime = parsedTo.getTime();
      if (fromTime > toTime) {
        this.toastService.show(
          'error',
          'Invalid range',
          'Upload start date cannot be greater than Upload end date',
        );
        return;
      }
    }

    // Copy current inputs to applied filters and trigger filtering
    this.appliedSearchText = this.searchText;
    this.appliedStatusFilter = this.statusFilter;
    this.appliedDocumentTypeFilter = this.isPfeuConnector ? this.documentTypeFilter : '';
    this.appliedUploadedByFilter = this.isVcfConnector ? this.uploadedByFilter : '';
    // Only apply date filters when both dates provided; otherwise clear any applied date filters
    // Apply the parsed dates if both were provided; prefer freshly parsed values
    this.appliedCreatedFrom = fromTyped && toTyped ? parsedFrom || this.createdFrom : null;
    this.appliedCreatedTo = fromTyped && toTyped ? parsedTo || this.createdTo : null;
    this.pageIndex = 0;
    this.onFilterChange();

    // Clear any existing row selections when filters are applied so checked
    // batches do not persist across different filtered views (e.g. when
    // switching from Error_Extraction -> Verify). Persist the cleared
    // checked list to BatchStateService as well.
    if (this.selection && this.selection.length > 0) {
      this.clearSelections();
      try {
        const current = this.batchStateService.getState();
        this.batchStateService.saveState({ ...current, checkedBatches: [] });
      } catch (e) {
        // ignore
      }
    }

    // Do not show a "No matching records found" notification here; table will simply show no rows
  }

  public onAction(action: 'verify' | 'reprocess' | 'delete' | 'clearOperator', batch: Batch): void {
    // Close any open inline popover immediately when an action is chosen
    this.openPopoverBatchId = null;
    // Save current state before navigation
    if (action === 'verify') {
      this.saveCurrentState(batch.batchId);
    }

    // If user selected the action 'verify' from the per-row menu, go directly
    // to the verify screen instead of opening the bottom confirmation footer.
    if (action === 'verify') {
      // Ensure single selection is set so callers expecting selection state remain consistent
      this.selection = [batch];
      this.selectionChange.emit(this.selection);
      // Emit action to parent for navigation/handling
      this.batchAction.emit({ action: 'verify', batch });
      return;
    }

    // If user selected 'reprocess' from the per-row menu, trigger it immediately
    // (do not open the bottom action footer)
    if (action === 'reprocess') {
      // If a selection exists and connector is VCF, handle bulk reprocess for selected batches
      if (this.selection.length > 0 && this.isVcfConnector) {
        this.showActionFooter = false;
        this.pendingAction = null;
        this.onBulkAction('reprocess');
        return;
      }

      // Do not open the bottom footer when reprocessing from the per-row menu
      this.showActionFooter = false;
      this.pendingAction = null;
      // Trigger reprocess immediately for this single batch (do not alter checkbox selection)
      this.reprocessBatch(batch);
      return;
    }

    // For clearOperator (Claim Batch) we show a confirmation dialog
    // whether triggered from the per-row menu or the bottom footer.
    if (action === 'clearOperator') {
      // If there is an active selection (footer) just open the footer as before
      if (this.selection.length > 0) {
        this.pendingAction = action;
        this.showActionFooter = true;
        return;
      }

      // No selection (per-row click): prepare selection and show claim dialog
      this.selection = [batch];
      this.selectionChange.emit(this.selection);
      this.batchesToClaim = [batch];
      this.showClaimDialog = true;
      return;
    }

    // If user has selected batches via checkboxes, show bottom bar for bulk actions
    if (this.selection.length > 0) {
      this.pendingAction = action;
      this.showActionFooter = true;
      return;
    }

    // No checkbox selections: perform the action immediately for the single batch
    // but keep the bulk-action API path so behavior is consistent (e.g. delete shows confirmation dialog)
    this.selection = [batch];
    this.selectionChange.emit(this.selection);
    // Delegate to the bulk handler which will either show a confirmation dialog or perform the action
    this.onBulkAction(action);
  }

  public onBulkAction(action: 'verify' | 'reprocess' | 'delete' | 'clearOperator' | 'download'): void {
    if (action === 'download') {
      // BULK DOWNLOAD HANDLED HERE
      if (this.canDownloadForSelection()) {
        this.onDownloadSelectedBatch();
      }

      // If we showed a confirmation dialog because metadata is incomplete,
      // keep the selection/ticker state so checkboxes remain checked while dialog is open.
      if (!this.showDownloadConfirmDialog && !this.showHoldDownloadDialog) {
        // Reset footer state when no confirmation is needed
        this.showActionFooter = false;
        this.pendingAction = null;
        this.selection = [];
        this.selectionChange.emit(this.selection);
      } else {
        // Ensure footer remains visible and pending action preserved while dialog is open
        this.pendingAction = 'download';
        this.showActionFooter = true;
      }

      return;
    }

    if (action === 'delete') {
      // For delete action, show confirmation dialog
      this.batchesToDelete = [...this.selection];
      this.showDeleteDialog = true;
    } else if (action === 'verify') {
      // For verify action, only allow one batch at a time
      if (this.selection.length > 0) {
        const batchToVerify = this.selection[0];
        if (this.canUserVerifyBatch(batchToVerify)) {
          // Save current state so selection and paging are preserved when returning
          this.saveCurrentState(batchToVerify.batchId);
          this.batchAction.emit({ action, batch: batchToVerify });
        }
      }
      return; // Don't emit to parent - handled internally
    } else if (action === 'clearOperator') {
      // For clear operator, only process the first selected batch (restriction: one at a time)
      if (this.selection.length > 0) {
        // Show confirmation dialog before clearing the operator
        this.batchesToClaim = [this.selection[0]];
        this.showClaimDialog = true;
      }
      return; // Don't emit to parent - handled internally
    }else if (action === 'reprocess') {
      // For reprocess action: support bulk reprocess for VCF connector, otherwise single-batch only
      if (this.selection.length > 0) {
        const ids = this.selection.map((b) => b.batchId).filter(Boolean);
        if (ids.length === 0) return;
        this.captureFilterSnapshot();

        // Bulk reprocess for VCF connector - update rows in-place and preserve selection/filters
        if (this.isVcfConnector && this.selection.length > 1) {
          const requests = this.selection.map((b) => this.batchService.reprocessBatch(b.batchId));
          forkJoin(requests).subscribe({
            next: (results) => {
              this.toastService.show(
                'success',
                'Reprocessed',
                `${results.length} batch${results.length > 1 ? 'es' : ''} reprocessed`,
                5000,
              );

              // Refresh only the updated batches in-place to preserve applied filters, documentTypeFilter and selection
              const idsToRefresh = ids;
              const refreshReqs = idsToRefresh.map((id) => this.batchService.getBatchById(id));
              if (refreshReqs.length > 0) {
                forkJoin(refreshReqs).subscribe({
                  next: (updatedBatches: Batch[]) => {
                    const updatedMap = new Map(updatedBatches.map((u) => [u.batchId, u]));
                    this.batches = (this.batches || []).map((b) => {
                      const updated = updatedMap.get(b.batchId);
                      return updated ?? b;
                    });

                    // Rebuild dropdown lists without clearing applied filters
                    this.extractAvailableStatuses();
                    if (this.isPfeuConnector) this.extractAvailableDocumentTypes();

                    try { this.cd.detectChanges(); } catch (e) { /* ignore */ }
                    this.restoreFilterSnapshot();
                  },
                  error: (err) => {
                    // ignore detailed update errors; we've already reprocessed
                    console.error('Failed to refresh updated batches', err);
                    this.restoreFilterSnapshot();
                  },
                });
              } else {
                this.restoreFilterSnapshot();
              }

              // Preserve footer and checkbox selection so user can continue acting on same rows
              this.pendingAction = null;
              this.showActionFooter = this.selection.length > 0;
              this.selectionChange.emit(this.selection);
            },
            error: (err) => {
              console.error('Bulk reprocess failed', err);
              this.toastService.show('error', 'Error', 'Failed to reprocess selected batches.');
              this.restoreFilterSnapshot();
            },
          });

          // Do NOT clear selection or call onRefresh() here (preserve filters and selection)
          return;
        }

        // Non-VCF or single selection: reprocess first selected batch
        const batchToReprocess = this.selection[0];
        this.reprocessBatch(batchToReprocess);
        // Preserve footer and selection so the user can continue actions on the same row.
        this.pendingAction = null;
        this.showActionFooter = this.selection.length > 0;
        this.selectionChange.emit(this.selection);
      }
      return; // Don't emit to parent - handled internally
    } else {
      // For other actions, emit individual events
      this.selection.forEach((batch) => {
        this.batchAction.emit({ action, batch });
      });
    }

    // Reset footer state
    this.showActionFooter = false;
    this.pendingAction = null;
    this.selection = [];
    this.selectionChange.emit(this.selection);
  }

  public cancelAction(): void {
    this.showActionFooter = false;
    this.pendingAction = null;
  }

  public clearSelections(): void {
    this.selection = [];
    this.showActionFooter = false;
    this.selectionChange.emit(this.selection);
  }

  private getCurrentUser(): void {
    this.currentUser = this.resolveCurrentUserName();
  }

  private resolveCurrentUserName(): string {
    try {
      const active =
        this.msalService.instance.getActiveAccount() ||
        this.msalService.instance.getAllAccounts()[0];

      if (active) {
        // Try different account properties
        const claims =
          ((active as { idTokenClaims?: Record<string, unknown> } | null)?.idTokenClaims) || {};
        const claimPreferred = claims['preferred_username'];
        const claimEmail = claims['email'];
        return (
          active.name ||
          active.username ||
          (typeof claimPreferred === 'string' ? claimPreferred : undefined) ||
          (typeof claimEmail === 'string' ? claimEmail : undefined) ||
          active.localAccountId ||
          ''
        );
      }
    } catch (e) {
      return '';
    }

    return '';
  }

  public canUserVerifyBatch(batch: Batch): boolean {
    // If we've determined from the pages API that this batch has no pages, hide Verify
    if (batch && batch.batchId && this.pagesAvailable[batch.batchId] === false) return false;

    const status = (batch.status || '').toString().toLowerCase();
    const operator = (batch.operator || '').toString();
    // Verify option logic:
    // Hide verify option for extraction/password errors and New status
    if (status === 'error_extraction' || status === 'password_error' || status === 'new') {
      return false;
    }
    // 1. If no operator assigned: unknown user can verify the batch (regardless of status)
    // 2. If operator assigned: only that user can verify, and only if status is "Hold" or "Error_Export"
    // 3. Allow verification for completed batches (read-only mode)
    if (!operator) return true; // No operator assigned - any user can verify
     if (status === 'completed' || status === 'downloaded') return true; // Completed batches can be viewed by anyone (read-only)
      // Treat Error_Export and Export(ed) similarly to Hold: the assigned operator may verify it
      return (
        status === 'hold' ||
        status === 'error_export' ||
        status === 'export' ||
        status === 'exported'
      ) && this.isSameUser(operator, this.currentUser);
    }
  

  public hasOperator(batch: Batch): boolean {
    return !!(batch.operator && batch.operator.trim());
  }

  private isSameUser(operator: string | undefined | null, current: string | undefined | null): boolean {
    try {
      const op = this.nameFormatService.formatName(operator || '').toString().trim().toLowerCase();
      const cur = this.nameFormatService.formatName(current || '').toString().trim().toLowerCase();
      if (!op || !cur) return false;
      return op === cur;
    } catch (e) {
      try {
        const op2 = (operator || '').toString().trim().toLowerCase();
        const cur2 = (current || '').toString().trim().toLowerCase();
        return !!op2 && op2 === cur2;
      } catch (ee) {
        return false;
      }
    }
  }

  public canClaimBatch(batch: Batch): boolean {
    if (!batch) return false;
    const status = (batch.status || '').toString().toLowerCase();
    if (status === 'completed' || status === 'complete' || status === 'success') return false;
    const operator = (batch.operator || '').toString();
    // If the operator/updated-by is the current user and the batch is on hold
    // or in Error_Export, the user should Verify the batch instead of being offered Claim.
    if (
      operator &&
      this.currentUser &&
      (status === 'hold' || status === 'error_export' || status === 'export' || status === 'exported')
    ) {
      if (this.isSameUser(operator, this.currentUser)) return false;
    }
    return this.hasOperator(batch);
  }

  public canClearOperatorForSelection(): boolean {
    // Only show Clear Operator button if:
    // 1. Exactly one batch is selected (restriction: one at a time)
    // 2. That batch has an operator
    if (this.selection.length !== 1) {
      return false;
    }

    // Find the full batch object from the batches array
    const selectedBatchId = this.selection[0].batchId;
    const fullBatch = this.batches.find((batch) => batch.batchId === selectedBatchId);

    if (!fullBatch) {
      return false;
    }

    const status = (fullBatch.status || '').toString().toLowerCase();
    if (status === 'completed' || status === 'complete' || status === 'success' || status === 'downloaded') return false;
    const operator = (fullBatch.operator || '').toString();
    // For Hold: only allow Claim when operator exists and is NOT the current user
    if (status === 'hold') {
      if (!operator) return false;
      return !this.isSameUser(operator, this.currentUser);
    }

    if (
      operator &&
      this.currentUser &&
      (status === 'error_export' || status === 'export' || status === 'exported')
    ) {
      if (this.isSameUser(operator, this.currentUser)) return false;
    }

    return this.hasOperator(fullBatch);
  }

  public canVerifyForSelection(): boolean {
  if (this.selection.length !== 1) return false;

  const selectedBatchId = this.selection[0].batchId;
  const fullBatch = this.batches.find((batch) => batch.batchId === selectedBatchId);

  if (!fullBatch) return false;

  const status = (fullBatch.status || '').toLowerCase();

  // Special-case: when status is 'hold', allow Verify only if the assigned operator
  // (updatedBy) matches the currently logged-in user. Otherwise do not show Verify.
  if (status === 'hold') {
    const operator = (fullBatch.operator || '').toString();
    // If no operator assigned, allow Verify for Hold
    if (!operator || operator.trim() === '') return true;
    if (this.currentUser) {
      return this.isSameUser(operator, this.currentUser);
    }
    return false;
  }

  // For other statuses, defer to canUserVerifyBatch which contains the broader rules
  return this.canUserVerifyBatch(fullBatch);
}

  public canReprocessForSelection(): boolean {
    // For VCF connector allow reprocessing of multiple selected batches
    if (this.isVcfConnector) {
      return this.selection.length > 0;
    }

    // Default behaviour: only allow when exactly one batch is selected and not completed
    if (this.selection.length !== 1) {
      return false;
    }

    // Find the full batch object from the batches array
    const selectedBatchId = this.selection[0].batchId;
    const fullBatch = this.batches.find((batch) => batch.batchId === selectedBatchId);

    if (!fullBatch) {
      return false;
    }

    const status = (fullBatch.status || '').toString().toLowerCase();
    if (status === 'downloaded') return true;
    return status !== 'completed';
  }

  public isReprocessPrimaryForSelection(): boolean {
    if (this.pendingAction === 'reprocess') return true;
    // If explicitly chosen, respect pending action
    // If multiple selected, highlight Reprocess when any selected batch is in an extraction/error/new state
    if (this.selection.length > 1) {
      const special = ['error_extraction', 'error extraction', 'password_error', 'password error', 'new'];
      for (const s of this.selection) {
        const st = ((s && s.status) || '').toString().toLowerCase();
        if (special.includes(st)) return true;
      }
      return false;
    }

    // Single selection path (existing behavior)
    if (this.selection.length !== 1) return false;

    const selectedBatchId = this.selection[0].batchId;
    const fullBatch = this.batches.find((batch) => batch.batchId === selectedBatchId);
    if (!fullBatch) return false;

    // If pages API indicates there are no pages for this batch, make Reprocess primary
    if (selectedBatchId && this.pagesAvailable[selectedBatchId] === false) return true;

    const status = (fullBatch.status || '').toString().toLowerCase();
    return ['error_extraction', 'password_error', 'new'].includes(status);
  }

  public isDownloadPrimaryForSelection(): boolean {
    // For VCF connector highlight Download only when the selection supports
    // download and Verify is not available (Verify takes precedence).
    if (!this.isVcfConnector) return false;
    if (!this.canDownloadForSelection()) return false;
    // If Verify or Claim (clear operator) are available for the current selection,
    // prefer those actions as primary over Download.
    if (this.canVerifyForSelection() || this.canClearOperatorForSelection()) return false;
    return true;
  }

  public getVerifyLabel(batch: Batch): string {
    const status = (batch && batch.status ? batch.status : '').toString().toLowerCase();
    // For Project Finance EU (PFEU/VCF) connector, completed batches should show
    // "View batch details" instead of "Verify" (matches Agency Desk behaviour).
    if (this.isPfeuConnector && status === 'completed') return 'View batch details';
    // Default label for other statuses/connectors remains 'Verify'
    return 'Verify';

  }

  public getVerifyLabelForSelection(): string {
    if (this.selection.length !== 1) return 'Verify';
    const selectedBatchId = this.selection[0].batchId;
    const fullBatch = this.batches.find((batch) => batch.batchId === selectedBatchId);
    if (!fullBatch) return 'Verify';
    return this.getVerifyLabel(fullBatch);
  }

  public claimBatch(batch: Batch): void {
    const claimingUserName = this.resolveCurrentUserName();
    if (!claimingUserName) {
      this.toastService.show('error', 'Error', 'Unable to identify current user. Please try again.');
      return;
    }

    this.currentUser = claimingUserName;
    const updatedBatch: Batch = {
      ...batch,
      operator: claimingUserName,
    };

    // Call the update API
    this.batchService.updateBatch(batch.batchId, updatedBatch).subscribe({
      next: (result) => {
        // Update the local batch data
        const batchIndex = this.batches.findIndex((b) => b.batchId === batch.batchId);
        if (batchIndex !== -1) {
          this.batches[batchIndex] = result;
        }

        // If the batch is in Hold status after update, navigate to Verify
        // without showing a toast. Otherwise show the usual success toast.
        const status = (result?.status || '').toString().toLowerCase();
        // Treat both Hold and Error_Export as navigable to Verify (same behavior as Claim -> Hold)
        if (status === 'hold' || status === 'error_export') {
          try {
            this.saveCurrentState(result.batchId);
          } catch (e) {
            /* ignore */
          }
          // Ensure parent handlers navigate to Verify screen
          this.batchAction.emit({ action: 'verify', batch: result });
        } else {
          // Show success notification (match the Claim Batch label)
          this.toastService.show(
            'success',
            'Claimed',
            `Batch claimed successfully: ${batch.batchName || batch.batchId}`,
            5000,
          );
        }

        // Reset footer state if this was a bulk action
        this.showActionFooter = false;
        this.pendingAction = null;
        this.selection = [];
        this.selectionChange.emit(this.selection);
      },
      error: (error) => {
        // Show error notification to the user
        console.error('Failed to claim batch', error);
        this.toastService.show('error', 'Error', 'Failed to claim batch. Please try again.');
      },
    });
  }

  public reprocessBatch(batch: Batch): void {
    if (!batch || !batch.batchId) return;
    this.captureFilterSnapshot();
    // Ensure the batch is selected (checkbox ticked) in the table
    this.selection = [{ ...batch }];
    this.selectionChange.emit(this.selection);

    // Highlight the selected batch row
    this.selectedBatchId = batch.batchId;

    this.batchService.reprocessBatch(batch.batchId).subscribe({
      next: (res) => {
        // Show success notification including the batch name or id
        this.toastService.show('success', 'Reprocessed', `Document successfully reprocessed`, 5000);

        // Fetch the updated batch and update only that row in the table so
        // other rows/columns are not affected by a full refresh.
        try {
          this.batchService.getBatchById(batch.batchId).subscribe({
            next: (updated) => {
              const idx = this.batches.findIndex((b) => b.batchId === updated.batchId);
              if (idx !== -1) {
                this.batches[idx] = updated;
              }
              // Also update any parent-cached batches in the service for consistency
              try {
                const svcIdx = this.batchService.batches?.findIndex?.(
                  (b) => b.batchId === updated.batchId,
                );
                if (svcIdx != null && svcIdx >= 0) {
                  this.batchService.batches[svcIdx] = updated;
                }
              } catch (e) {
                /* ignore */
              }
            },
            error: () => {
              /* ignore fetch error - we've already reprocessed successfully */
              this.restoreFilterSnapshot();
            },
          });
        } catch (e) {
          // ignore
          this.restoreFilterSnapshot();
        }

        this.restoreFilterSnapshot();

        // Clear highlighted selection after 5 seconds
        setTimeout(() => {
          if (this.selectedBatchId === batch.batchId) {
            this.selectedBatchId = '';
          }
        }, 5000);
      },
      error: (err) => {
        console.error('Failed to reprocess batch', err);
        this.toastService.show('error', 'Error', 'Failed to reprocess batch. Please try again.');
        this.restoreFilterSnapshot();
        // Clear highlight on error as well after short delay
        setTimeout(() => {
          if (this.selectedBatchId === batch.batchId) {
            this.selectedBatchId = '';
          }
        }, 2000);
      },
    });
  }

  private saveCurrentState(selectedBatchId: string): void {
    // Add the batch to selection if not already selected
    if (!this.isSelected({ batchId: selectedBatchId } as Batch)) {
      this.selection.push({ batchId: selectedBatchId } as Batch);
    }

    // Prefer applied filters (the ones that were active when user clicked Search).
    // Fall back to the current input values if applied ones are not set.
    const state: BatchListState = {
      pageIndex: this.pageIndex,
      pageSize: this.pageSize,
      searchText: this.appliedSearchText || this.searchText || '',
      statusFilter: this.appliedStatusFilter || this.statusFilter || '',
      documentTypeFilter: this.isPfeuConnector
        ? (this.appliedDocumentTypeFilter || this.documentTypeFilter || '')
        : '',
      uploadedByFilter: this.isVcfConnector
        ? (this.appliedUploadedByFilter || this.uploadedByFilter || '')
        : '',
      createdFromDate: this.appliedCreatedFrom
        ? this.formatStateDate(this.appliedCreatedFrom)
        : this.createdFromDate || '',
      createdToDate: this.appliedCreatedTo
        ? this.formatStateDate(this.appliedCreatedTo)
        : this.createdToDate || '',
      sortField: this.sortField,
      sortOrder: this.sortOrder,
      selectedBatch: selectedBatchId,
      checkedBatches: this.selection.map((batch) => batch.batchId),
    };
    this.batchStateService.saveState(state);
  }

  private restoreState(): void {
    const state = this.batchStateService.getState();
    if (state) {
      this.pageIndex = state.pageIndex || 0;
      this.pageSize = state.pageSize || 10;
      // Restore both visible inputs and the applied filter values so filters are active on return
      this.searchText = state.searchText || '';
      this.statusFilter = state.statusFilter || '';
      this.createdFromDate = state.createdFromDate || '';
      this.createdToDate = state.createdToDate || '';

      // Also set applied filters from saved state and parse date strings to Date objects
      this.appliedSearchText = state.searchText || '';
      this.appliedStatusFilter = state.statusFilter || '';
      // Restore saved Document Type filter (PFEU-only). Keep both the input and applied values in sync.
      this.documentTypeFilter = state.documentTypeFilter || '';
      this.appliedDocumentTypeFilter = state.documentTypeFilter || '';
      this.uploadedByFilter = state.uploadedByFilter || '';
      this.appliedUploadedByFilter = state.uploadedByFilter || '';
      this.appliedCreatedFrom = this.parseFilterDate(state.createdFromDate || '') || null;
      this.appliedCreatedTo = this.parseFilterDate(state.createdToDate || '') || null;

      // Ensure parsed createdFrom/To (used by inputs) are set as well
      this.createdFrom = this.parseFilterDate(this.createdFromDate) || null;
      this.createdTo = this.parseFilterDate(this.createdToDate) || null;
      this.sortField = state.sortField;
      this.sortOrder = state.sortOrder || null;
      this.selectedBatchId = state.selectedBatch || '';

      // Restore checkbox selections
      if (state.checkedBatches && state.checkedBatches.length > 0) {
        if (this.batches && this.batches.length > 0) {
          // Batches already loaded, restore immediately
          this.restoreCheckboxSelections(state.checkedBatches);
        } else {
          // Store for later restoration when batches are loaded
          this.pendingCheckboxRestoration = state.checkedBatches;
        }
      }

      // Emit page change to update pagination
      if (state.pageIndex !== undefined) {
        this.pageChange.emit(state.pageIndex);
      }

      // Apply restored filters immediately so the list reflects previously active filters
      setTimeout(() => this.applyFilters(), 0);
    }
  }

  /** Row-level helpers so the inline popover matches footer logic */
  public canVerifyOnRow(batch: Batch): boolean {
    if (!batch) return false;
    // If user has a multi-selection elsewhere, require single-row operations
    if (this.selection.length > 1) return false;

    const status = (batch.status || '').toString().toLowerCase();
    if (status === 'hold') {
      const operator = (batch.operator || '').toString();
      if (!operator || operator.trim() === '') return true;
      if (this.currentUser) return this.isSameUser(operator, this.currentUser);
      return false;
    }

    return this.canUserVerifyBatch(batch);
  }

  public canClearOperatorOnRow(batch: Batch): boolean {
    if (!batch) return false;
    // Disallow when multiple selection present
    if (this.selection.length > 1) return false;

    const status = (batch.status || '').toString().toLowerCase();
    if (status === 'completed' || status === 'complete' || status === 'success' || status === 'downloaded') return false;
    const operator = (batch.operator || '').toString();
    if (status === 'hold') {
      if (!operator) return false;
      return !this.isSameUser(operator, this.currentUser);
    }

    if (
      operator &&
      this.currentUser &&
      (status === 'error_export' || status === 'export' || status === 'exported')
    ) {
      if (this.isSameUser(operator, this.currentUser)) return false;
    }

    return this.hasOperator(batch);
  }

  public canReprocessOnRow(batch: Batch): boolean {
    if (!batch) return false;
    // Disable per-row reprocess when multiple rows are selected
    if (this.selection.length > 1) return false;
    const status = (batch.status || '').toString().toLowerCase();
    if (status === 'downloaded') return true;
    return status !== 'completed';
  }

  private saveCurrentStateNoSelection(): void {
    const state: BatchListState = {
      pageIndex: this.pageIndex,
      pageSize: this.pageSize,
      searchText: this.appliedSearchText || this.searchText || '',
      statusFilter: this.appliedStatusFilter || this.statusFilter || '',
      documentTypeFilter: this.isPfeuConnector
        ? (this.appliedDocumentTypeFilter || this.documentTypeFilter || '')
        : '',
      uploadedByFilter: this.isVcfConnector
        ? (this.appliedUploadedByFilter || this.uploadedByFilter || '')
        : '',
      createdFromDate: this.appliedCreatedFrom
        ? this.formatStateDate(this.appliedCreatedFrom)
        : this.createdFromDate || '',
      createdToDate: this.appliedCreatedTo
        ? this.formatStateDate(this.appliedCreatedTo)
        : this.createdToDate || '',
      sortField: this.sortField,
      sortOrder: this.sortOrder,
      selectedBatch: this.selectedBatchId, // Keep existing selection
      checkedBatches: this.selection.map((batch) => batch.batchId),
    };
    this.batchStateService.saveState(state);
  }

  private restoreCheckboxSelections(checkedBatchIds: string[]): void {
    // Clear current selection
    this.selection = [];

    // Find and select batches that match the stored IDs
    this.batches.forEach((batch) => {
      if (checkedBatchIds.includes(batch.batchId)) {
        this.selection.push(batch);
      }
    });

    // Show action footer if we restored any selections
    this.showActionFooter = this.selection.length > 0;

    // If we restored a selection, ensure the table shows the page containing
    // the first selected batch so the user sees the same row they inspected.
    if (this.selection.length > 0) {
      const firstSelected = this.selection[0];
      const sorted = this.sortedBatches;
      const idx = sorted.findIndex((b) => b.batchId === firstSelected.batchId);
      if (idx >= 0 && this.pageSize > 0) {
        const newPageIndex = Math.floor(idx / this.pageSize);
        if (newPageIndex !== this.pageIndex) {
          this.pageIndex = Math.min(Math.max(0, newPageIndex), Math.max(0, this.pageCount - 1));
          // Notify any listeners that the page changed so pagination UI updates
          try {
            this.pageChange.emit(this.pageIndex);
          } catch (e) {
            /* ignore */
          }
        }
      }
    }

    // Emit selection change
    this.selectionChange.emit(this.selection);
  }

  // Format a Date to a compact state string (YYYY-MM-DD) for saving in BatchState
  private formatStateDate(d: Date): string {
    if (!d) return '';
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  }

  public onSort(col: ColumnConfig): void {
    if (col.sortable === false) return;
    const field = col.field?.toString();
    if (!field) return;
    if (this.sortField !== field) {
      this.sortField = field;
      this.sortOrder = 'asc';
    } else {
      this.sortOrder = this.sortOrder === 'asc' ? 'desc' : 'asc';
    }
    // Reset to first page to avoid empty pages after order change
    this.pageIndex = 0;
  }

  public isSorted(col: ColumnConfig, order: 'asc' | 'desc'): boolean {
    const field = col.field?.toString();
    return !!field && this.sortField === field && this.sortOrder === order;
  }

  public getStatusVariant(value: unknown): string {
    const v = (value ?? '').toString().toLowerCase().trim();
    switch (v) {
      case 'verify':
        return 'info';
      case 'new':
        return ''; // Default tag (no variant)
      case 'completed':
      case 'complete':
      case 'success':
        return ''; // Default tag (no variant) - or could be 'info' if you prefer
      case 'hold':
      case 'on hold':
        return 'warning';
      case 'error_extraction':
      case 'error extraction':
        return 'error';
      default:
        return ''; // Default tag (no variant)
    }
  }

  public getStatusClasses(value: unknown): string[] {
    const v = (value ?? '').toString().toLowerCase().trim();
    const classes: string[] = ['status-pill'];
    switch (v) {
      case 'verify':
        classes.push('rds-color-background-info', 'status-pill-bg');
        break;
      case 'new':
        classes.push('rds-menu-color-background-subtle', 'status-pill-bg');
        break;
      case 'completed':
      case 'complete':
      case 'success':
        classes.push('rds-color-background-success', 'status-pill-bg');
        break;
      case 'hold':
      case 'on hold':
        classes.push('rds-color-background-warning', 'status-pill-bg');
        break;
      case 'error_extraction':
      case 'error extraction':
        classes.push('rds-color-background-danger', 'status-pill-bg');
        break;
      default:
        classes.push('rds-menu-color-background-subtle', 'status-pill-bg');
        break;
    }
    return classes;
  }

  public isSelected(batch: Batch): boolean {
    return this.selection.some((b) => b.batchId === batch.batchId);
  }

  public getConnectorDisplayName(): string {
    const connectorOptions = this.configService.getConnectorOptions();
    const option = connectorOptions.find((opt) => opt.value === this.connector);
    return option?.viewValue || this.connector;
  }

  public onRefresh(event?: Event): void {
    // Prevent default anchor navigation (avoid returning to auth)
    try {
      event?.preventDefault();
    } catch (e) {
      /* ignore */
    }

    if (this.refreshInProgress) {
      return;
    }

    this.refreshInProgress = true;
    // Perform an in-component refresh by fetching batches directly
    // Preserve current checkbox selection so it can be restored after refresh
    const preservedCheckedIds = (this.selection || []).map((s) => s.batchId).filter(Boolean);
    try {
      this.clearFilters();
    } catch (e) {
      /* ignore */
    }
    // Reset pagination to initial defaults on refresh
    this.pageSize = this.initialPageSize || 10;
    this.pageIndex = 0;
    try {
      this.pageChange.emit(this.pageIndex);
    } catch (e) {
      /* ignore */
    }

    // Reset sorting to connector default on refresh and persist state
    try {
      this.sortField = this.defaultSort?.field;
      this.sortOrder = this.defaultSort?.order ?? null;
      try {
        this.saveCurrentStateNoSelection();
      } catch (e) {
        /* ignore */
      }
    } catch (e) {
      // ignore
    }

    this.batchService.getAllBatches(this.connector).subscribe({
      next: (data: Batch[]) => {
        this.refreshInProgress = false;
        if (!data || data.length === 0) {
          this.batches = [];
          this.batchService.batches = [];
          this.toastService.show('info', 'No Records', 'No records found for selected connector');
          return;
        }
        this.batches = data;
        this.batchService.batches = data;
        this.extractAvailableStatuses();
        // Rebuild Document Type list (PFEU only) so new incoming types are visible
        if (this.isPfeuConnector) {
          this.extractAvailableDocumentTypes();
        }
        if (this.isVcfConnector) {
          this.extractAvailableUploadedBy();
        }
        // Restore any previously-checked batches that still exist in the refreshed data
        if (preservedCheckedIds && preservedCheckedIds.length > 0) {
          const availableIds = new Set((data || []).map((d) => d.batchId));
          const toRestore = preservedCheckedIds.filter((id) => availableIds.has(id));
          if (toRestore.length > 0) {
            this.restoreCheckboxSelections(toRestore);
          } else {
            // If nothing to restore, ensure UI selection is cleared
            this.clearSelections();
          }
        }
        this.showSingleRefreshSuccessToast();
      },
      error: (err: unknown) => {
        this.refreshInProgress = false;
        console.error('Error fetching batches:', err);
        this.toastService.show('error', 'Error', 'Failed to refresh batches');
      },
    });
  }

  private showSingleRefreshSuccessToast(): void {
    if (this.refreshToastId !== null) {
      try {
        this.toastService.dismiss(this.refreshToastId);
      } catch (e) {
        /* ignore */
      }
      this.refreshToastId = null;
    }

    this.refreshToastId = this.toastService.show('success', 'Refreshed', 'Batches refreshed successfully!');
  }

  // Notifications are handled via ToastService (global outlet)

  public toggleMoreOptions(): void {
    this.showMoreOptions = !this.showMoreOptions;
  }

  public onAccountsClick(): void {
    this.showMoreOptions = false;
    this.moreOptionsAction.emit('accounts');
  }

  public onPasswordClick(): void {
    this.showMoreOptions = false;
    this.moreOptionsAction.emit('password');
  }

  @HostListener('document:click', ['$event'])
  public onDocumentClick(event: Event): void {
    const target = event.target as HTMLElement;
    const moreOptionsSection = target.closest('.more-options-section');

    if (!moreOptionsSection && this.showMoreOptions) {
      this.showMoreOptions = false;
    }
    // Close inline action popover when clicking outside of action area
    const clickedInAction = !!(
      target.closest('.actions-column') ||
      target.closest('.inline-popover') ||
      target.closest('.action-button')
    );
    if (!clickedInAction && this.openPopoverBatchId) {
      this.openPopoverBatchId = null;
    }
  }

  public togglePopover(batch: Batch, event: Event): void {
    // Prevent document click handler from immediately closing the popover
    event.stopPropagation();
    if (!batch || !batch.batchId) return;
    // Ensure we know whether pages exist for this batch so Verify option can be hidden when needed
    this.ensurePagesLoaded(batch.batchId);
    if (this.openPopoverBatchId === batch.batchId) {
      this.openPopoverBatchId = null;
    } else {
      this.openPopoverBatchId = batch.batchId;
    }
  }

  public isPopoverOpen(batch: Batch): boolean {
    return !!(batch && batch.batchId && this.openPopoverBatchId === batch.batchId);
  }

  public shouldOpenPopoverAbove(batch: Batch, index: number): boolean {
    if (!batch) return false;

    const status = (batch.status || '').toString().trim().toLowerCase();
    const isErrorExtraction = status === 'error_extraction' || status === 'error extraction';
    if (isErrorExtraction) {
      const visibleCount = this.displayedBatches?.length || 0;
      return visibleCount > 0 && index >= Math.max(visibleCount - 2, 0);
    }

    return index >= 5;
  }

  public toggleSelection(batch: Batch): void {
    const index = this.selection.findIndex((b) => b.batchId === batch.batchId);
    if (index >= 0) {
      this.selection.splice(index, 1);
    } else {
      this.selection.push(batch);
    }

    // When user checks/unchecks batches, show or hide the action footer
    this.showActionFooter = this.selection.length > 0;

    // Close any open per-row inline popover when entering selection mode
    if (this.selection.length > 0 && this.openPopoverBatchId) {
      this.openPopoverBatchId = null;
    }

    this.selectionChange.emit(this.selection);
    // If single selection, ensure pages availability is known for the selected batch
    if (this.selection.length === 1 && this.selection[0]?.batchId) {
      this.ensurePagesLoaded(this.selection[0].batchId);
    }
  }

  public isAllSelected(): boolean {
    const page = this.displayedBatches;
    return page.length > 0 && page.every((b) => this.isSelected(b));
  }

  public masterToggle(): void {
    const page = this.displayedBatches;
    if (this.isAllSelected()) {
      // remove current page items from selection
      const pageIds = new Set(page.map((b) => b.batchId));
      this.selection = this.selection.filter((s) => !pageIds.has(s.batchId));
    } else {
      // add all current page items not already selected
      const currentIds = new Set(this.selection.map((s) => s.batchId));
      const toAdd = page.filter((b) => !currentIds.has(b.batchId));
      this.selection = [...this.selection, ...toAdd];
    }

    // When user toggles master checkbox, show or hide the footer based on selection
    this.showActionFooter = this.selection.length > 0;

    // Close any open per-row inline popover when entering selection mode
    if (this.selection.length > 0 && this.openPopoverBatchId) {
      this.openPopoverBatchId = null;
    }

    this.selectionChange.emit(this.selection);
    // If exactly one selected after master toggle (edge-case), ensure pages availability
    if (this.selection.length === 1 && this.selection[0]?.batchId) {
      this.ensurePagesLoaded(this.selection[0].batchId);
    }
  }

  private ensurePagesLoaded(batchId: string): void {
    if (!batchId) return;
    // If we already cached availability, no need to call API again
    if (this.pagesAvailable[batchId] !== undefined) return;

    // Optimistically leave undefined until result comes back. On error, treat as having pages (do not hide Verify).
    this.batchService.getAllPages(batchId).subscribe({
      next: (pages) => {
        try {
          this.pagesAvailable[batchId] = Array.isArray(pages) && pages.length === 0 ? false : true;
          try {
            this.cd.detectChanges();
          } catch (e) {
            /* ignore */
          }
        } catch (e) {
          this.pagesAvailable[batchId] = true;
        }
      },
      error: () => {
        // On error, assume pages exist so we don't incorrectly hide Verify
        this.pagesAvailable[batchId] = true;
        try {
          this.cd.detectChanges();
        } catch (e) {
          /* ignore */
        }
      },
    });
  }

  /**
   * Handle clicks on the per-row action trigger. When any checkbox selection
   * exists, disable per-row actions to avoid conflicting UX between bulk
   * selection and per-row actions.
   */
  public onActionButtonClick(batch: Batch, event: Event): void {
    // When multiple rows are selected, disable the per-row three-dots actions to avoid conflicting UX.
    if (this.selection.length > 1) {
      event.stopPropagation();
      return;
    }
    this.togglePopover(batch, event);
  }

  public onBatchNameClick(batch: Batch, event: Event): void {
    // Prevent row click handlers from firing
    event.stopPropagation();
    try {
      (event as Event).preventDefault?.();
    } catch (e) {
      /* ignore */
    }

    // Allow navigation even when in bulk selection mode — clicking the name should open Verify

    // If the batch is in a state where Verify should not navigate (e.g. New, Error, Password_Error,
    // or extraction/password errors), open the bottom action footer instead so the user can
    // choose reprocess/delete/claim.
    const status = (batch?.status || '').toString().toLowerCase();
    const forbidden = [
      'new',
      'error',
      'password_error',
      'password error',
      'error_extraction',
      'error extraction',
    ];
    if (forbidden.includes(status)) {
      // Select this single batch and open the action footer (bulk-style UI for single item)
      this.selection = [batch];
      this.selectionChange.emit(this.selection);
      this.showActionFooter = true;
      return;
    }

    // If batch is on Hold and operator/updated-by is set to another user,
    // do not navigate directly — select the batch and open the footer so
    // the current user can choose an action. If operator matches current
    // user (or no operator), proceed to verify as usual.
    if (status === 'hold') {
      const operatorRaw = (batch.operator || '').toString().trim();
      if (operatorRaw && !this.isSameUser(operatorRaw, this.currentUser)) {
        // Another user has the batch on hold — open action footer instead
        this.selection = [batch];
        this.selectionChange.emit(this.selection);
        this.showActionFooter = true;
        return;
      }
    }

    // Delegate to the same handler as the per-row Verify action. The
    // parent/component handling `batchAction` will decide whether to open
    // the Verify screen or show read-only details for completed batches.
    this.onAction('verify', batch);
  }

  // Normalize various inputs to a Date object for DatePipe
  public toDate(
    value: unknown,
    assumeUtcForNaiveIso = false,
    dayFirstForSlash = false,
  ): Date | null {
    if (!value && value !== 0) return null;
    // Already a Date
    if (value instanceof Date && !isNaN(value.getTime())) {
      return value;
    }
    // Epoch milliseconds
    if (typeof value === 'number') {
      const d = new Date(value);
      return isNaN(d.getTime()) ? null : d;
    }
    // String parsing
    if (typeof value === 'string') {
      const s = value.trim();
      // Naive ISO-like strings without timezone (e.g. 2026-07-20T14:10:00 or 2026-07-20 14:10:00)
      // can originate from backend UTC timestamps; convert from UTC for display when requested.
      const naiveIso = s.match(
        /^(\d{4})-(\d{2})-(\d{2})(?:[T\s](\d{1,2}):(\d{2})(?::(\d{2}))?)?$/,
      );
      if (naiveIso) {
        const year = parseInt(naiveIso[1], 10);
        const month = parseInt(naiveIso[2], 10) - 1;
        const day = parseInt(naiveIso[3], 10);
        const hour = naiveIso[4] ? parseInt(naiveIso[4], 10) : 0;
        const minute = naiveIso[5] ? parseInt(naiveIso[5], 10) : 0;
        const second = naiveIso[6] ? parseInt(naiveIso[6], 10) : 0;

        const d = assumeUtcForNaiveIso
          ? new Date(Date.UTC(year, month, day, hour, minute, second))
          : new Date(year, month, day, hour, minute, second);
        return isNaN(d.getTime()) ? null : d;
      }

      // Slash/dash date-time without timezone (e.g. 07/20/2026 15:41 or 20/07/2026 15:41)
      // For backend UTC payloads, convert by constructing from UTC.
      const slashDateTime = s.match(
        /^(\d{1,2})[\/\-](\d{1,2})[\/\-](\d{4})(?:\s+(\d{1,2}):(\d{2})(?::(\d{2}))?)?$/,
      );
      if (slashDateTime) {
        const p1 = parseInt(slashDateTime[1], 10);
        const p2 = parseInt(slashDateTime[2], 10);
        const year = parseInt(slashDateTime[3], 10);
        const hour = slashDateTime[4] ? parseInt(slashDateTime[4], 10) : 0;
        const minute = slashDateTime[5] ? parseInt(slashDateTime[5], 10) : 0;
        const second = slashDateTime[6] ? parseInt(slashDateTime[6], 10) : 0;

        // Resolve ambiguous day/month using explicit format hint first, then numeric constraints.
        let dayFirst = dayFirstForSlash;
        if (!dayFirstForSlash) {
          if (p1 > 12 && p2 <= 12) dayFirst = true;
        }

        const day = dayFirst ? p1 : p2;
        const month = (dayFirst ? p2 : p1) - 1;

        const d = assumeUtcForNaiveIso
          ? new Date(Date.UTC(year, month, day, hour, minute, second))
          : new Date(year, month, day, hour, minute, second);
        return isNaN(d.getTime()) ? null : d;
      }

      // ISO or RFC formats
      if (s.includes('T') || s.includes('Z')) {
        const d = new Date(s);
        return isNaN(d.getTime()) ? null : d;
      }
      // dd/MM/yyyy HH:mm[:ss]
      const m = s.match(
        /^(\d{1,2})\/(\d{1,2})\/(\d{4})(?:\s+(\d{1,2}):(\d{1,2})(?::(\d{1,2}))?)?$/,
      );
      if (m) {
        const day = parseInt(m[1], 10);
        const month = parseInt(m[2], 10) - 1;
        const year = parseInt(m[3], 10);
        const hour = m[4] ? parseInt(m[4], 10) : 0;
        const minute = m[5] ? parseInt(m[5], 10) : 0;
        const second = m[6] ? parseInt(m[6], 10) : 0;
        const d = new Date(year, month, day, hour, minute, second);
        return isNaN(d.getTime()) ? null : d;
      }
      // Fallback to Date.parse
      const d = new Date(s);
      return isNaN(d.getTime()) ? null : d;
    }
    return null;
  }

  public toDisplayDate(value: unknown, field?: string, format?: string): Date | null {
    const normalizedField = (field || '').toLowerCase();
    const isBackendTimestamp = normalizedField === 'datetime' || normalizedField === 'lastmodifieddate';
    const isDayFirstInput = isBackendTimestamp || (format || '').toLowerCase().startsWith('dd/');
    return this.toDate(value, isBackendTimestamp, isDayFirstInput);
  }

  public getCellValue(batch: Batch, column: ColumnConfig): unknown {
    if (column.type === 'metadata') {
      return this.getMetadataValue(batch, column.metadataField);
    }

    const fields = column.field.toString().split('.');
    let value: unknown = batch;
    for (const field of fields) {
      if (value === null || value === undefined) return '';
      value = (value as Record<string, unknown>)[field];
    }

    // If this is the uploadedBy column prefer batch.uploadedBy then fallback to senderAddress
    if (column.field === 'uploadedBy') {
      const uploadedRaw = (batch.uploadedBy || batch.senderAddress || value || '');
      return this.nameFormatService.formatName(uploadedRaw);
    }

    // If this is the operator column, present a friendly "First Last" format
    if (column.field === 'operator') {
      return this.nameFormatService.formatName(value);
    }

    // If the value looks like an email address, display it in lower-case
    if (typeof value === 'string' && value.includes('@')) {
      return value.toLowerCase();
    }

    // If this column appears to be a document type field and we're in VCF,
    // canonicalize known types or blank unsupported values so the UI doesn't
    // display unrelated strings like "ORIGINAL".
    try {
      const fieldName = (column.field || '').toString().toLowerCase();
      const isDocumentField =
        fieldName === 'documenttype' || fieldName === 'document' || fieldName === 'doctype' || fieldName.includes('document');
      if (this.isVcfConnector && isDocumentField) {
        const raw = (value || '').toString();
        const compact = raw.replace(/\s+/g, '').toUpperCase();
        if (compact === 'SEAWAYBILL') return 'SEA WAYBILL';
        if (compact === 'BILLOFLADING') return 'BILL OF LADING';
        return '';
      }
    } catch (e) {
      // ignore formatting errors and fall through to return the original value
    }

    return value;
  }

  /**
   * Format operator/updated-by strings into a more human-friendly form.
   * Examples:
   *  - "Reddy, S (Suresh)" => "Suresh Reddy"
   *  - "Doe, John" => "John Doe"
   * Falls back to the original string when it doesn't match known patterns.
   */
  public formatOperator(input: unknown): string {
    console.log('Formatting operator value: 3725', input);
    return this.nameFormatService.formatName(input);
  }

  public getMetadataValue(batch: Batch, field?: string): string {
    if (!field || !batch.metadata) return '';
    const normalizedRequestedField = this.normalizeMetadataFieldName(field);
    const aliasFields = (() => {
      if (normalizedRequestedField === 'documentcompliance') return ['compliancestatus'];
      if (normalizedRequestedField === 'compliancestatus') return ['documentcompliance'];
      return [] as string[];
    })();

    const metadataField =
      batch.metadata.find((m) => m.field === field) ||
      batch.metadata.find((m) => {
        const normalizedMetadataField = this.normalizeMetadataFieldName(m.field);
        return (
          normalizedMetadataField === normalizedRequestedField ||
          aliasFields.includes(normalizedMetadataField)
        );
      });
    const raw = metadataField?.value || '';

    if (normalizedRequestedField === 'compliancestatus' || normalizedRequestedField === 'documentcompliance') {
      const compact = raw.toString().trim();
      if (!compact) return '';

      const firstToken = compact.split(/[\s-]+/)[0]?.trim() || compact;
      if (/^(yes|no)$/i.test(firstToken)) {
        return firstToken.charAt(0).toUpperCase() + firstToken.slice(1).toLowerCase();
      }

      return compact.toLowerCase().startsWith('yes') ? 'Yes' : compact.toLowerCase().startsWith('no') ? 'No' : compact;
    }

    // If this is a VCF connector and the metadata field is a document type,
    // canonicalize to the allowed labels or return blank for unsupported values.
    if (this.isVcfConnector && raw) {
      const normalizedField = (field || '').toString().toLowerCase().replace(/[^a-z0-9]/g, '');
      const isDocumentField =
        normalizedField === 'documenttype' ||
        normalizedField === 'doctype' ||
        normalizedField === 'document';
      if (isDocumentField) {
        const compact = raw.replace(/\s+/g, '').toUpperCase();
        if (compact === 'SEAWAYBILL') return 'SEA WAYBILL';
        if (compact === 'BILLOFLADING') return 'BILL OF LADING';
        return '';
      }
    }

    return raw;
  }

  public onConfirmDialogDismiss(event: Event | CustomEvent<unknown> | Record<string, unknown>): void {
    const eventRecord = event as Record<string, unknown>;
    const payload =
      (eventRecord['detail'] as Record<string, unknown> | undefined) ?? eventRecord;
    const confirmed = payload?.['confirmed'] === true || payload?.['confirmed'] === 'true';
    // Ensure the dialog state is closed
    this.showDeleteDialog = false;
    if (confirmed) {
      // Proceed with deletion
      this.confirmDelete();
    } else {
      // Cancelled by user
      this.cancelDelete();
    }
  }

  public onClaimDialogDismiss(event: Event | CustomEvent<unknown> | Record<string, unknown>): void {
    const eventRecord = event as Record<string, unknown>;
    const payload =
      (eventRecord['detail'] as Record<string, unknown> | undefined) ?? eventRecord;
    const confirmed = payload?.['confirmed'] === true || payload?.['confirmed'] === 'true';
    // Ensure the dialog state is closed
    this.showClaimDialog = false;
    if (confirmed) {
      this.confirmClaim();
    } else {
      this.cancelClaim();
    }
  }

  public cancelClaim(): void {
    this.showClaimDialog = false;
    this.batchesToClaim = [];
    // Keep selection as-is; user clicked Go back
    // Show the bottom action footer so the user can continue with bulk actions
    this.pendingAction = 'clearOperator';
    this.showActionFooter = true;
  }

  public confirmClaim(): void {
    if (!this.batchesToClaim || this.batchesToClaim.length === 0) return;
    const batch = this.batchesToClaim[0];
    this.batchesToClaim = [];
    this.showClaimDialog = false;
    this.claimBatch(batch);
  }

  public cancelDelete(): void {
    this.showDeleteDialog = false;
    this.batchesToDelete = [];
  }

  public confirmDelete(): void {
    if (this.batchesToDelete.length === 0) return;

    const deleteRequests = this.batchesToDelete.map((batch) =>
      this.batchService.deleteBatch(batch.batchId),
    );

    forkJoin(deleteRequests).subscribe({
      next: () => {
        this.showDeleteDialog = false;

        // Show success notification
        const count = this.batchesToDelete.length;
        this.toastService.show(
          'success',
          'Deleted',
          `Successfully deleted ${count} batch${count > 1 ? 'es' : ''}`,
          5000,
        );

        // Get deleted batch IDs
        const deletedIds = new Set(this.batchesToDelete.map((b) => b.batchId));

        // Immediately remove deleted batches from local array for instant UI update
        this.batches = this.batches.filter((batch) => !deletedIds.has(batch.batchId));

        // Clear selection since deleted batches are no longer available
        this.selection = this.selection.filter((batch) => !deletedIds.has(batch.batchId));
        this.selectionChange.emit(this.selection);

        // Rebuild Document Type list (PFEU only) so the filter dropdown reflects deletions
        if (this.isPfeuConnector) {
          this.extractAvailableDocumentTypes();
          this.extractAvailableUploadedBy();
          // If the currently applied document type is no longer present, clear it
          if (
            this.appliedDocumentTypeFilter &&
            !this.availableDocumentTypes.includes(this.appliedDocumentTypeFilter)
          ) {
            this.documentTypeFilter = '';
            this.appliedDocumentTypeFilter = '';
            // Ensure UI/pagination updates when filters change
            this.onFilterChange();
          }
          if (
            this.appliedUploadedByFilter &&
            !this.availableUploadedBy.includes(this.appliedUploadedByFilter)
          ) {
            this.uploadedByFilter = '';
            this.appliedUploadedByFilter = '';
            this.onFilterChange();
          }
        }

        // Rebuild Status list so the status dropdown reflects deletions
        this.extractAvailableStatuses();
        if (
          this.appliedStatusFilter &&
          !this.availableStatuses.includes(this.appliedStatusFilter)
        ) {
          this.statusFilter = '';
          this.appliedStatusFilter = '';
          this.onFilterChange();
        }

        // Also trigger server refresh to ensure data consistency (but UI already updated)
        this.onRefresh();

        // Clear the deletion list
        this.batchesToDelete = [];

        // notification auto-hides via helper
      },
      error: (err) => {
        this.showDeleteDialog = false;
        this.batchesToDelete = [];
      },
    });
  }

  private generateExportFilename(): string {
  const now = new Date();
  const yyyy = now.getFullYear();
  const MM = String(now.getMonth() + 1).padStart(2, '0');
  const dd = String(now.getDate()).padStart(2, '0');
  const hh = String(now.getHours()).padStart(2, '0');
  const mm = String(now.getMinutes()).padStart(2, '0');
  const ss = String(now.getSeconds()).padStart(2, '0');

  return `BL_Export_${yyyy}${MM}${dd}_${hh}${mm}${ss}.xlsx`;
}

public canRowDownload(batch: Batch): boolean {
  const status = (batch.status ?? '').toLowerCase();
  return this.isVcfConnector &&
    (status === 'verify' || status === 'downloaded' || status === 'hold' || status === 'error_export' || status === 'error export');
}

public onDownloadSingleBatch(batch: Batch): void {
  if (!this.canRowDownload(batch)) return;

    // If batch is on Hold and owned by another user, block download and show dialog
    try {
      const status = (batch.status ?? '').toString().toLowerCase();
      const owner = (batch.uploadedBy || batch.operator || '').toString().trim();
      if (status === 'hold' && owner && !this.isSameUser(owner, this.currentUser)) {
        // ensure the clicked row remains selected in the UI
        this.selection = [batch];
        try { this.selectionChange.emit(this.selection); } catch (e) {}
        this.pendingAction = 'download';
        this.showActionFooter = true;
        this.holdOwnerMessage = this.nameFormatService.formatName(owner);
        this.holdMessagePrefix = 'The batch is currently on hold';
        this.showHoldDownloadDialog = true;
        return;
      }
    } catch (e) {
      // ignore and continue
    }

  if (this.prepareDownloadComplianceWarning([batch])) {
    return;
  }

  this.performExport([batch]);
}


public canDownloadForSelection(): boolean {
  if (!this.isVcfConnector) return false;
  if (this.selection.length === 0) return false;
  return this.selection.every(b => {
    const status = (b.status ?? '').toLowerCase();
    return (
      status === 'verify' ||
      status === 'downloaded' ||
      status === 'hold' ||
      status === 'error_export' ||
      status === 'error export'
    );
  });
}



public onDownloadSelectedBatch(): void {
  if (!this.isVcfConnector || this.selection.length === 0) return;

  const verifyBatches = this.selection.filter(
    b => (b.status ?? '').toLowerCase() === 'verify' || (b.status ?? '').toLowerCase() === 'downloaded' || (b.status ?? '').toLowerCase() === 'hold'
  );

  if (verifyBatches.length === 0) return;
  // If any selected batch is on Hold and owned by another user, block download and show dialog
  const offendingHold = verifyBatches.filter(b => {
    const status = (b.status ?? '').toString().toLowerCase();
    const owner = (b.uploadedBy || b.operator || '').toString().trim();
    return status === 'hold' && owner && !this.isSameUser(owner, this.currentUser);
  });
  if (offendingHold.length > 0) {
    // collect unique owners
    const owners = Array.from(new Set(offendingHold.map(b => (b.uploadedBy || b.operator || '').toString().trim()))).filter(o => o);
    // ensure the UI selection reflects the batches user attempted to download
    this.selection = verifyBatches;
    try { this.selectionChange.emit(this.selection); } catch (e) {}
    this.pendingAction = 'download';
    this.showActionFooter = true;
    if (owners.length === 1) {
      this.holdOwnerMessage = this.nameFormatService.formatName(owners[0]);
      this.holdMessagePrefix = 'The batch is currently on hold';
    } else if (owners.length > 1) {
      // join up to 3 names, then indicate there are more if necessary
      const formattedOwners = Array.from(
        new Set(owners.map((owner) => this.nameFormatService.formatName(owner)).filter((owner) => owner)),
      );
      const display = formattedOwners.length <= 3
        ? formattedOwners.join(', ')
        : `${formattedOwners.slice(0, 3).join(', ')} and ${formattedOwners.length - 3} ${formattedOwners.length - 3 === 1 ? 'other' : 'others'}`;
      this.holdOwnerMessage = display;
      this.holdMessagePrefix = 'The batches are currently on hold';
    }
    this.showHoldDownloadDialog = true;
    return;
  }
  if (this.prepareDownloadComplianceWarning(verifyBatches)) {
    return;
  }

  this.performExport(verifyBatches);
}

  private prepareDownloadComplianceWarning(batches: Batch[]): boolean {
    const nonCompliantBatches = batches.filter((batch) => this.hasDownloadComplianceWarning(batch));

    if (nonCompliantBatches.length === 0) {
      return false;
    }

    this.pendingDownloadBatches = batches;
    this.downloadIncompleteCount = nonCompliantBatches.length;
    const batchLabel = nonCompliantBatches.length === 1 ? '1 batch' : `${nonCompliantBatches.length} batches`;
    const fieldLabel = nonCompliantBatches.length === 1 ? 'Field is missing' : 'Fields are missing';
    this.downloadConfirmMessage = `We noticed in ${batchLabel} - ${fieldLabel} or not meeting compliance check. You can still download them, but the files may be incomplete. Would you like to continue?`;
    this.showDownloadConfirmDialog = true;
    return true;
  }

  private hasDownloadComplianceWarning(batch: Batch): boolean {
    const complianceStatus = this.getDownloadComplianceStatus(batch);
    return complianceStatus !== 'yes';
  }

  private getDownloadComplianceStatus(batch: Batch): string {
    if (!batch?.metadata?.length) return '';

    const field = batch.metadata.find(
      (item) => this.normalizeMetadataFieldName(item.field) === 'compliancestatus',
    );

    return (field?.value ?? '').toString().trim().toLowerCase();
  }

  private normalizeMetadataFieldName(value: string | null | undefined): string {
    return (value ?? '').toString().replace(/[\s_]+/g, '').toLowerCase();
  }

  // Helper: determine if a batch has incomplete/missing metadata
  public hasIncompleteMetadata(batch: Batch): boolean {
    if (!batch) return false;
    if (!batch.metadata || batch.metadata.length === 0) return true;
    return batch.metadata.some(m => (m.value ?? '').toString().trim() === '');
  }

  // Perform export for the provided batches (assumes permission checks done)
  public performExport(batches: Batch[]): void {
    if (!batches || batches.length === 0) return;
    const batchIds = batches.map(b => b.batchId).join(',');

    this.batchService.exportVcfBatch(batchIds).subscribe({
      next: (blob: Blob) => {
        const filename = this.generateExportFilename();

        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);

        // Update local status for all downloaded batches (no API call for user-triggered download)
        const downloadedIds = new Set<string>();
        batches.forEach(batch => {
          downloadedIds.add(batch.batchId);
        });

        // Also update local batch list so UI reflects the new status immediately
        if (this.batches && this.batches.length > 0) {
          this.batches = this.batches.map(b =>
            downloadedIds.has(b.batchId) ? { ...b, status: 'Downloaded' } : b,
          );
          try {
            this.cd.detectChanges();
          } catch (e) {
            // ignore detectChanges errors
          }
        }

        this.toastService.show('success', 'Downloaded', 'Export completed successfully.');

        // Keep selection and footer open after download so users can continue
        // working with the same selection. Previously we cleared selection
        // here for multi-batch exports which caused the footer to close.
      },
      error: () => {
        this.toastService.show('error', 'Error', 'Failed to download file.');
      }
    });
  }

  public onDownloadDialogDismiss(event: Event | CustomEvent<unknown> | Record<string, unknown>): void {
    const eventRecord = event as Record<string, unknown>;
    const payload =
      (eventRecord['detail'] as Record<string, unknown> | undefined) ?? eventRecord;
    const confirmed = payload?.['confirmed'] === true || payload?.['confirmed'] === 'true';
    // Ensure dialog state is closed
    this.showDownloadConfirmDialog = false;
    if (confirmed) {
      // Proceed with previously selected batches
      this.performExport(this.pendingDownloadBatches || []);
      this.pendingDownloadBatches = [];
      this.downloadIncompleteCount = 0;
      this.downloadConfirmMessage = '';
    } else {
      // Cancel
      this.cancelDownload();
    }
  }

  public onHoldDialogDismiss(event: unknown): void {
    // simply close the informational dialog and restore selection/footer state
    this.showHoldDownloadDialog = false;
    this.holdOwnerName = '';
    this.holdOwnerMessage = '';
    this.holdMessagePrefix = '';
    // Keep footer in download pending state so user can act on other selections
    this.pendingAction = 'download';
    this.showActionFooter = true;
  }

  public cancelDownload(): void {
    this.showDownloadConfirmDialog = false;
    this.pendingDownloadBatches = [];
    this.downloadIncompleteCount = 0;
    this.downloadConfirmMessage = '';
    // restore footer so user can continue with bulk actions
    this.pendingAction = 'download';
    this.showActionFooter = true;
  }

}
